// ─── Language ─────────────────────────────────────────────────────────────────
// The page's language is whatever Shopify rendered the document in. Arabic
// is this bundle's source text, so on an Arabic page __ez() hands every
// string straight back; on an English page it looks the string up in the
// dictionary the layout loads beside this file (eazio-landing.i18n.en.js).
// A string with no entry falls back to the Arabic rather than to nothing.
const EZ_LANG = String(document.documentElement.lang || "ar").toLowerCase().indexOf("en") === 0 ? "en" : "ar";
const EZ_RTL = EZ_LANG !== "en";
const EZ_STRINGS = EZ_LANG === "en" && window.EAZIO_I18N_EN || {};
const __ez = s => typeof s === "string" && Object.prototype.hasOwnProperty.call(EZ_STRINGS, s) ? EZ_STRINGS[s] : s;

// ─── Motion preference ────────────────────────────────────────────────────────
const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const ease = "cubic-bezier(0.16,1,0.3,1)";
const easeIO = "cubic-bezier(0.65,0,0.35,1)";

// ─── useScrollReveal ──────────────────────────────────────────────────────────
const useScrollReveal = (ref, {
  threshold = 0.1,
  margin = "-80px 0px"
} = {}) => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    if (prefersReducedMotion) {
      setVisible(true);
      return;
    }
    const el = ref?.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, {
      threshold,
      rootMargin: margin
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
};

// ─── useCounter ───────────────────────────────────────────────────────────────
const useCounter = (target, active) => {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    if (!active || target === 0) return;
    if (prefersReducedMotion) {
      setVal(target);
      return;
    }
    const start = performance.now();
    const dur = 1800;
    const tick = now => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target]);
  return val;
};

// ─── useBreakpoint ────────────────────────────────────────────────────────────
const useBreakpoint = () => {
  const [width, setWidth] = React.useState(() => typeof window !== "undefined" ? window.innerWidth : 1280);
  React.useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener("resize", fn, {
      passive: true
    });
    return () => window.removeEventListener("resize", fn);
  }, []);
  return {
    isMobile: width < 768,
    isTablet: width < 1024,
    width
  };
};

// ─── Cart Context ─────────────────────────────────────────────────────────────
const CartContext = React.createContext(null);
const useCart = () => React.useContext(CartContext);
const CartProvider = ({
  children
}) => {
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const fetchCart = async () => {
    const res = await fetch('/cart.js');
    const data = await res.json();
    setItems(data.items || []);
  };
  React.useEffect(() => {
    fetchCart();
  }, []);
  const addToCart = async variantId => {
    setLoading(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      if (!res.ok) throw new Error('cart error');
      await fetchCart();
      setOpen(true);
    } catch (e) {
      console.error('[eazio] cart error:', e);
    } finally {
      setLoading(false);
    }
  };
  const removeItem = async key => {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    });
    await fetchCart();
  };
  const updateQty = async (key, qty) => {
    if (qty < 1) {
      removeItem(key);
      return;
    }
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: qty
      })
    });
    await fetchCart();
  };
  const total = items.reduce((s, i) => s + i.final_line_price, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  return /*#__PURE__*/React.createElement(CartContext.Provider, {
    value: {
      items,
      open,
      setOpen,
      addToCart,
      removeItem,
      updateQty,
      total,
      count,
      loading
    }
  }, children);
};

// ─── Cart Drawer ──────────────────────────────────────────────────────────────
const CartDrawer = () => {
  const {
    items,
    open,
    setOpen,
    removeItem,
    updateQty,
    total,
    count
  } = useCart();
  const fmt = cents => `$${(cents / 100).toFixed(0)}`;
  return /*#__PURE__*/React.createElement(React.Fragment, null, open && /*#__PURE__*/React.createElement("div", {
    onClick: () => setOpen(false),
    style: {
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      background: "rgba(2,1,5,0.65)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      animation: "fadeIn 0.2s ease"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      width: "min(420px, 100vw)",
      zIndex: 1001,
      background: "#0d0b1a",
      borderLeft: "1px solid rgba(255,255,255,0.08)",
      transform: open ? "translateX(0)" : "translateX(100%)",
      transition: `transform 0.38s cubic-bezier(0.16,1,0.3,1)`,
      display: "flex",
      flexDirection: "column",
      boxShadow: open ? "-24px 0 80px rgba(0,0,0,0.6)" : "none"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "20px 24px",
      borderBottom: "1px solid rgba(255,255,255,0.07)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 10
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 24 24",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z",
    stroke: "#7b6ef0",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "3",
    y1: "6",
    x2: "21",
    y2: "6",
    stroke: "#7b6ef0",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 10a4 4 0 01-8 0",
    stroke: "#7b6ef0",
    strokeWidth: "1.5",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 18,
      fontWeight: 700,
      color: "#FAFAFA"
    }
  }, __ez("سلة الطلبات")), count > 0 && /*#__PURE__*/React.createElement("span", {
    style: {
      background: "#3b2dbc",
      color: "#fff",
      borderRadius: 100,
      padding: "2px 9px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 12,
      fontWeight: 700
    }
  }, count)), /*#__PURE__*/React.createElement("button", {
    onClick: () => setOpen(false),
    style: {
      width: 32,
      height: 32,
      borderRadius: 8,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "rgba(250,250,250,0.5)",
      cursor: "pointer",
      fontSize: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "×")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: "auto",
      padding: "16px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 10
    }
  }, items.length === 0 ? /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      padding: "60px 0"
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "48",
    height: "48",
    viewBox: "0 0 24 24",
    fill: "none",
    style: {
      margin: "0 auto 16px",
      opacity: 0.2
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z",
    stroke: "white",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "3",
    y1: "6",
    x2: "21",
    y2: "6",
    stroke: "white",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 10a4 4 0 01-8 0",
    stroke: "white",
    strokeWidth: "1.5",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      color: "rgba(250,250,250,0.3)",
      margin: 0
    }
  }, __ez("السلة فارغة")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "rgba(250,250,250,0.18)",
      marginTop: 6
    }
  }, __ez("أضف منتجات من القائمة أدناه"))) : items.map(item => /*#__PURE__*/React.createElement("div", {
    key: item.key,
    style: {
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 10,
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 600,
      color: "#FAFAFA",
      marginBottom: 2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }
  }, item.product_title), item.variant_title && item.variant_title !== "Default Title" && /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      color: "rgba(250,250,250,0.38)",
      marginBottom: 4
    }
  }, item.variant_title), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 700,
      color: "#7b6ef0"
    }
  }, fmt(item.final_price))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => updateQty(item.key, item.quantity - 1),
    style: {
      width: 26,
      height: 26,
      borderRadius: 6,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "#FAFAFA",
      cursor: "pointer",
      fontSize: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "−"), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      color: "#FAFAFA",
      minWidth: 18,
      textAlign: "center"
    }
  }, item.quantity), /*#__PURE__*/React.createElement("button", {
    onClick: () => updateQty(item.key, item.quantity + 1),
    style: {
      width: 26,
      height: 26,
      borderRadius: 6,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "#FAFAFA",
      cursor: "pointer",
      fontSize: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "+")), /*#__PURE__*/React.createElement("button", {
    onClick: () => removeItem(item.key),
    style: {
      background: "transparent",
      border: "none",
      color: "rgba(250,250,250,0.25)",
      cursor: "pointer",
      padding: 4,
      display: "flex",
      alignItems: "center",
      transition: "color 0.15s"
    },
    onMouseEnter: e => e.currentTarget.style.color = "#ff6b6b",
    onMouseLeave: e => e.currentTarget.style.color = "rgba(250,250,250,0.25)"
  }, /*#__PURE__*/React.createElement("svg", {
    width: "14",
    height: "14",
    viewBox: "0 0 14 14",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2 2l10 10M12 2L2 12",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round"
  })))))), items.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "20px 24px",
      borderTop: "1px solid rgba(255,255,255,0.07)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      color: "rgba(250,250,250,0.45)"
    }
  }, __ez("المجموع الكلي")), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 22,
      fontWeight: 800,
      color: "#FAFAFA"
    }
  }, fmt(total))), /*#__PURE__*/React.createElement("a", {
    href: "/checkout",
    style: {
      display: "block",
      width: "100%",
      background: "#3b2dbc",
      color: "#FAFAFA",
      textDecoration: "none",
      textAlign: "center",
      borderRadius: 10,
      padding: "14px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 700,
      boxShadow: "0 4px 20px rgba(59,45,188,0.45)",
      transition: "transform 0.18s ease, box-shadow 0.18s ease"
    },
    onMouseEnter: e => {
      e.currentTarget.style.transform = "translateY(-1px)";
      e.currentTarget.style.boxShadow = "0 8px 28px rgba(59,45,188,0.6)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(59,45,188,0.45)";
    }
  }, __ez("إتمام الشراء ←")))));
};

// ─── Cart Floating Button ─────────────────────────────────────────────────────
const CartFloatBtn = () => {
  const {
    count,
    open,
    setOpen,
    loading
  } = useCart();
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": __ez("سلة الطلبات") + (count > 0 ? " (" + count + ")" : ""),
    "aria-expanded": open,
    onClick: () => setOpen(!open),
    style: {
      position: "fixed",
      bottom: 28,
      left: 28,
      zIndex: 999,
      width: 56,
      height: 56,
      borderRadius: "50%",
      background: loading ? "#2a1f8a" : "#3b2dbc",
      border: "none",
      cursor: "pointer",
      boxShadow: "0 4px 24px rgba(59,45,188,0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease"
    },
    onMouseEnter: e => {
      e.currentTarget.style.transform = "scale(1.08)";
      e.currentTarget.style.boxShadow = "0 8px 32px rgba(59,45,188,0.7)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "0 4px 24px rgba(59,45,188,0.55)";
    }
  }, loading ? /*#__PURE__*/React.createElement("div", {
    style: {
      width: 22,
      height: 22,
      border: "2px solid rgba(255,255,255,0.3)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite"
    }
  }) : /*#__PURE__*/React.createElement("svg", {
    width: "22",
    height: "22",
    viewBox: "0 0 24 24",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z",
    stroke: "white",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "3",
    y1: "6",
    x2: "21",
    y2: "6",
    stroke: "white",
    strokeWidth: "2",
    strokeLinecap: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 10a4 4 0 01-8 0",
    stroke: "white",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), count > 0 && /*#__PURE__*/React.createElement("span", {
    style: {
      position: "absolute",
      top: -4,
      right: -4,
      minWidth: 20,
      height: 20,
      borderRadius: 100,
      padding: "0 5px",
      background: "#ff4d00",
      color: "#fff",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(255,77,0,0.5)"
    }
  }, count));
};

// ─── RevealSection ────────────────────────────────────────────────────────────
const RevealSection = ({
  children,
  delay = 0,
  y = 28,
  style = {}
}) => {
  const ref = React.useRef(null);
  const visible = useScrollReveal(ref);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: {
      ...style,
      opacity: visible ? 1 : 0,
      transform: `translateY(${visible ? 0 : y}px)`,
      transition: prefersReducedMotion ? "none" : `opacity 0.7s ${ease} ${delay}s, transform 0.7s ${ease} ${delay}s`
    }
  }, children);
};

// ─── AnimatedMeshBg ───────────────────────────────────────────────────────────
const AnimatedMeshBg = () => /*#__PURE__*/React.createElement("div", {
  style: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none"
  }
}, /*#__PURE__*/React.createElement("div", {
  style: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(90px)",
    width: "65vw",
    height: "55vh",
    top: "-10%",
    left: "-5%",
    background: "rgba(59,45,188,0.45)",
    animation: prefersReducedMotion ? "none" : "meshFloat1 18s ease-in-out infinite"
  }
}), /*#__PURE__*/React.createElement("div", {
  style: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(100px)",
    width: "55vw",
    height: "65vh",
    top: "15%",
    right: "-10%",
    background: "rgba(83,68,164,0.3)",
    animation: prefersReducedMotion ? "none" : "meshFloat2 22s ease-in-out infinite"
  }
}), /*#__PURE__*/React.createElement("div", {
  style: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(80px)",
    width: "40vw",
    height: "40vh",
    bottom: "-15%",
    left: "35%",
    background: "rgba(111,105,169,0.2)",
    animation: prefersReducedMotion ? "none" : "meshFloat3 16s ease-in-out infinite"
  }
}), /*#__PURE__*/React.createElement("div", {
  className: "noise-overlay",
  style: {
    position: "absolute",
    inset: 0,
    opacity: 0.04
  }
}));

// ─── Logo ─────────────────────────────────────────────────────────────────────
const EazioMark = ({
  size = 36
}) => /*#__PURE__*/React.createElement("img", {
  src: window.EAZIO_LOGO_URL,
  alt: "eazio logo",
  style: {
    width: size,
    height: size,
    display: "block",
    objectFit: "contain"
  }
});
const EazioLogo = ({
  size = 28
}) => /*#__PURE__*/React.createElement("div", {
  style: {
    display: "flex",
    alignItems: "center"
  }
}, /*#__PURE__*/React.createElement(EazioMark, {
  size: size * 1.5
}));

// ─── Announcement Bar ─────────────────────────────────────────────────────────
const ANNOUNCEMENT_TEXT = __ez("كود خصم EAZIO10 — احصل على خصم 10% على أول طلب");
const AnnouncementBar = () => {
  const [visible, setVisible] = React.useState(true);
  const trackRef = React.useRef(null);
  const posRef = React.useRef(0);
  const rafRef = React.useRef(null);
  React.useEffect(() => {
    if (!visible || prefersReducedMotion) return;
    const SPEED = 0.55;
    const animate = () => {
      const track = trackRef.current;
      if (!track) return;
      posRef.current += SPEED;
      const halfW = track.scrollWidth / 2;
      if (posRef.current >= halfW) posRef.current -= halfW;
      track.style.transform = `translateX(${posRef.current}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible]);
  if (!visible) return null;
  const ITEM_STYLE = {
    fontFamily: "'IBM Plex Sans Arabic',sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: "rgba(255,255,255,0.9)",
    whiteSpace: "nowrap",
    flexShrink: 0
  };
  const DOT_STYLE = {
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.4)",
    flexShrink: 0
  };
  const repeat = [...Array(14)].map((_, i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: i
  }, /*#__PURE__*/React.createElement("span", {
    style: ITEM_STYLE
  }, ANNOUNCEMENT_TEXT), /*#__PURE__*/React.createElement("span", {
    style: DOT_STYLE
  })));
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 101,
      height: 36,
      background: "#3b2dbc",
      display: "flex",
      alignItems: "center",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: trackRef,
    style: {
      display: "flex",
      alignItems: "center",
      gap: 28,
      willChange: "transform",
      flexShrink: 0
    }
  }, repeat, repeat), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      cancelAnimationFrame(rafRef.current);
      setVisible(false);
    },
    style: {
      position: "absolute",
      insetInlineEnd: 12,
      top: "50%",
      transform: "translateY(-50%)",
      background: "rgba(0,0,0,0.25)",
      border: "none",
      color: "rgba(255,255,255,0.8)",
      cursor: "pointer",
      fontSize: 15,
      lineHeight: 1,
      padding: "3px 8px",
      borderRadius: 5,
      zIndex: 1
    }
  }, "×"));
};

// ─── Header ───────────────────────────────────────────────────────────────────
const Header = () => {
  const {
    isMobile
  } = useBreakpoint();
  const [scrolled, setScrolled] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, {
      passive: true
    });
    requestAnimationFrame(() => setMounted(true));
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const navLinks = [{
    label: __ez("خدمات"),
    href: "#services"
  }, {
    label: __ez("أعمال"),
    href: "#clients"
  }, {
    label: __ez("الأسعار"),
    href: "#pricing"
  }, {
    label: __ez("عنّا"),
    href: "#process"
  }, {
    label: __ez("تواصل"),
    href: "#cta"
  }];
  const discordUrl = window.EAZIO_CONFIG?.discordUrl || "#";
  return /*#__PURE__*/React.createElement("header", {
    style: {
      position: "fixed",
      top: 36,
      left: 0,
      right: 0,
      zIndex: 100,
      height: 72,
      display: "flex",
      alignItems: "center",
      padding: `0 ${isMobile ? "16px" : "40px"}`,
      background: scrolled ? "rgba(2,1,5,0.72)" : "transparent",
      backdropFilter: scrolled ? "blur(24px) saturate(1.5)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(24px) saturate(1.5)" : "none",
      borderBottom: scrolled ? "1px solid rgba(255,255,255,0.07)" : "1px solid transparent",
      transition: "background 0.35s ease, border-color 0.35s ease",
      opacity: mounted ? 1 : 0,
      transform: `translateY(${mounted ? 0 : -8}px)`,
      transitionProperty: "background, border-color, opacity, transform",
      transitionDuration: "0.35s, 0.35s, 0.5s, 0.5s",
      transitionTimingFunction: `ease, ease, ${ease}, ${ease}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: discordUrl,
    target: "_blank",
    rel: "noopener",
    style: {
      background: "#3b2dbc",
      color: "#FAFAFA",
      border: "none",
      borderRadius: 8,
      padding: "10px 20px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      boxShadow: "0 4px 16px rgba(59,45,188,0.4), 0 0 0 1px rgba(255,255,255,0.08) inset",
      transition: `transform 0.18s ${ease}, box-shadow 0.18s ease`
    },
    onMouseEnter: e => {
      e.currentTarget.style.transform = "translateY(-1px)";
      e.currentTarget.style.boxShadow = "0 6px 24px rgba(59,45,188,0.55)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(59,45,188,0.4)";
    }
  }, __ez("أطلب تصميمك الآن"))), !isMobile && /*#__PURE__*/React.createElement("nav", {
    style: {
      display: "flex",
      gap: 28,
      alignItems: "center"
    }
  }, navLinks.map(l => /*#__PURE__*/React.createElement("a", {
    key: l.label,
    href: l.href,
    onClick: e => {
      e.preventDefault();
      const el = document.querySelector(l.href);
      if (el) el.scrollIntoView({
        behavior: "smooth"
      });
    },
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      fontWeight: 500,
      color: "rgba(250,250,250,0.6)",
      textDecoration: "none",
      transition: "color 0.2s",
      cursor: "pointer"
    },
    onMouseEnter: e => e.target.style.color = "#FAFAFA",
    onMouseLeave: e => e.target.style.color = "rgba(250,250,250,0.6)"
  }, l.label))), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      display: "flex",
      justifyContent: "flex-end"
    }
  }, /*#__PURE__*/React.createElement(EazioLogo, {
    size: 44
  })));
};

// ─── StatItem ─────────────────────────────────────────────────────────────────
const StatItem = ({
  rawValue,
  label,
  active
}) => {
  const isPercent = rawValue.includes("%");
  const hasPlus = rawValue.includes("+");
  const num = parseInt(rawValue.replace(/[^0-9]/g, "")) || 0;
  const count = useCounter(num, active);
  const display = num > 0 ? `${count}${isPercent ? "%" : ""}${hasPlus ? "+" : ""}` : rawValue;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(22px,5vw,30px)",
      fontWeight: 800,
      letterSpacing: "-0.04em",
      color: "#FAFAFA",
      lineHeight: 1
    }
  }, display), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "rgba(250,250,250,0.4)",
      marginTop: 6
    }
  }, label));
};

// ─── Hero ─────────────────────────────────────────────────────────────────────
const Hero = () => {
  const {
    isMobile
  } = useBreakpoint();
  const [phase, setPhase] = React.useState(0);
  const statsRef = React.useRef(null);
  const statsVisible = useScrollReveal(statsRef, {
    margin: "0px"
  });
  const discordUrl = window.EAZIO_CONFIG?.discordUrl || "#";
  React.useEffect(() => {
    if (prefersReducedMotion) {
      setPhase(10);
      return;
    }
    const timers = [150, 250, 380, 550, 720].map((ms, i) => setTimeout(() => setPhase(i + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  const reveal = (minPhase, delay = 0) => ({
    opacity: phase >= minPhase ? 1 : 0,
    transform: `translateY(${phase >= minPhase ? 0 : 20}px)`,
    transition: prefersReducedMotion ? "none" : `opacity 0.75s ${ease} ${delay}s, transform 0.75s ${ease} ${delay}s`
  });
  return /*#__PURE__*/React.createElement("section", {
    style: {
      position: "relative",
      minHeight: "88vh",
      width: "100vw",
      marginLeft: "calc(50% - 50vw)",
      marginRight: "calc(50% - 50vw)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: isMobile ? "160px 20px 60px" : "180px 40px 80px",
      overflow: "hidden",
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement(AnimatedMeshBg, null), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      background: "linear-gradient(135deg, rgba(59,45,188,0.28) 0%, rgba(111,105,169,0.12) 40%, rgba(59,45,188,0.22) 80%, rgba(83,68,164,0.18) 100%)",
      backgroundSize: "300% 300%",
      animation: prefersReducedMotion ? "none" : "heroGradientShift 10s ease infinite"
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "hero-grid"
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "relative",
      maxWidth: 760,
      margin: "0 auto",
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 32,
      ...reveal(1)
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "rgba(59,45,188,0.15)",
      border: "1px solid rgba(59,45,188,0.35)",
      borderRadius: 100,
      padding: "6px 16px"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#7b6ef0",
      boxShadow: "0 0 8px #7b6ef0"
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      fontWeight: 500,
      color: "#a09dbf"
    }
  }, __ez("خدمات تصميم احترافية")))), /*#__PURE__*/React.createElement("h1", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(48px,7vw,84px)",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      lineHeight: 1.15,
      color: "#FAFAFA",
      margin: "0 0 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: reveal(2)
  }, __ez("خدمات مخصصة")), /*#__PURE__*/React.createElement("span", {
    style: {
      color: "#7b6ef0",
      ...reveal(3)
    }
  }, __ez("لتلبية احتياجاتك"))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 48,
      ...reveal(4)
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: "clamp(15px,1.8vw,18px)",
      fontWeight: 400,
      lineHeight: 1.85,
      color: "rgba(250,250,250,0.52)",
      margin: "0 auto",
      maxWidth: 540
    }
  }, __ez("استكشف منتجاتنا وأعمالنا المخصصة للحصول على تجربة فريدة."))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 14,
      justifyContent: "center",
      flexWrap: "wrap",
      ...reveal(5)
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: "#services",
    style: {
      background: "#3b2dbc",
      color: "#FAFAFA",
      border: "none",
      borderRadius: 10,
      padding: "15px 32px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 700,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      boxShadow: "0 4px 24px rgba(59,45,188,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset",
      transition: `transform 0.2s ${ease}, box-shadow 0.2s ease`
    },
    onMouseEnter: e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 10px 36px rgba(59,45,188,0.65)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "0 4px 24px rgba(59,45,188,0.5)";
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    style: {
      transform: EZ_RTL ? "scaleX(-1)" : "none"
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M3 8h10M9 4l4 4-4 4",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), __ez("استعرض الخدمات")), /*#__PURE__*/React.createElement("a", {
    href: discordUrl,
    target: "_blank",
    rel: "noopener",
    style: {
      background: "transparent",
      color: "rgba(250,250,250,0.7)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 10,
      padding: "15px 32px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 500,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      transition: "background 0.18s ease, border-color 0.18s ease, color 0.18s ease"
    },
    onMouseEnter: e => {
      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
      e.currentTarget.style.color = "#FAFAFA";
    },
    onMouseLeave: e => {
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
      e.currentTarget.style.color = "rgba(250,250,250,0.7)";
    }
  }, __ez("عرض الأعمال"))), /*#__PURE__*/React.createElement("div", {
    ref: statsRef,
    style: {
      display: "flex",
      gap: isMobile ? 20 : 48,
      justifyContent: "center",
      flexWrap: "wrap",
      marginTop: isMobile ? 48 : 72,
      paddingTop: isMobile ? 32 : 48,
      borderTop: "1px solid rgba(255,255,255,0.06)",
      opacity: statsVisible ? 1 : 0,
      transform: `translateY(${statsVisible ? 0 : 20}px)`,
      transition: prefersReducedMotion ? "none" : `opacity 0.7s ${ease}, transform 0.7s ${ease}`
    }
  }, /*#__PURE__*/React.createElement(StatItem, {
    rawValue: "240+",
    label: __ez("مشروع مُنجز"),
    active: statsVisible
  }), /*#__PURE__*/React.createElement(StatItem, {
    rawValue: "98%",
    label: __ez("رضا العملاء"),
    active: statsVisible
  }), /*#__PURE__*/React.createElement(StatItem, {
    rawValue: "5",
    label: __ez("أيام متوسط التسليم"),
    active: statsVisible
  }))));
};

// ─── Static categories & products ───────────────────────────────────────────
const CATEGORIES = [{
  id: "fivem",
  label: __ez("الفايف ام"),
  color: "#f97316",
  glow: "rgba(249,115,22,0.35)"
}, {
  id: "discord",
  label: __ez("الديسكورد"),
  color: "#5865F2",
  glow: "rgba(88,101,242,0.35)"
}, {
  id: "stream",
  label: __ez("الاستريم"),
  color: "#9146ff",
  glow: "rgba(145,70,255,0.35)"
}, {
  id: "stores",
  label: __ez("المتاجر الالكترونية"),
  color: "#10b981",
  glow: "rgba(16,185,129,0.35)"
}];
const INDIVIDUAL_PRODUCTS = {
  fivem: [{
    id: "f01",
    tag: "eazio-f01",
    name: __ez("شعار رسم"),
    type: "variant",
    priceA: 30,
    priceB: 50,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("3–5 أيام"),
    desc: __ez("شعار مرسوم يدويًا بأسلوب فني مميز لسيرفرك.")
  }, {
    id: "f02",
    tag: "eazio-f02",
    name: __ez("شعار رمزي"),
    type: "variant",
    priceA: 20,
    priceB: 30,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("2–4 أيام"),
    desc: __ez("شعار رمزي بسيط وقابل للتذكر.")
  }, {
    id: "f03",
    tag: "eazio-f03",
    name: __ez("شعار أحرف"),
    type: "variant",
    priceA: 15,
    priceB: 25,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–3 أيام"),
    desc: __ez("شعار مبني على أحرف اسم سيرفرك.")
  }, {
    id: "f04",
    tag: "eazio-f04",
    name: __ez("لودينج سكرين"),
    type: "variant",
    priceA: 30,
    priceB: 50,
    labelA: __ez("بسيط"),
    labelB: __ez("احترافي"),
    time: __ez("3–5 أيام"),
    desc: __ez("شاشة تحميل جذابة تُرحّب بلاعبيك عند الدخول.")
  }, {
    id: "f05",
    tag: "eazio-f05",
    name: __ez("بانر FiveM"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("بانر ترويجي لصفحة سيرفرك.")
  }, {
    id: "f06",
    tag: "eazio-f06",
    name: __ez("بانر تحميل"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("بانر شاشة التحميل داخل السيرفر.")
  }, {
    id: "f07",
    tag: "eazio-f07",
    name: __ez("إعلان FiveM"),
    type: "variant",
    priceA: 3,
    priceB: 5,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1 يوم"),
    desc: __ez("إعلان ترويجي للسيرفر على المنصات.")
  }, {
    id: "f08",
    tag: "eazio-f08",
    name: __ez("إنذار صوتي"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("تصميم واجهة الإنذار الصوتي في اللعبة.")
  }, {
    id: "f09",
    tag: "eazio-f09",
    name: __ez("جوال"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("تصميم واجهة الجوال داخل السيرفر.")
  }, {
    id: "f10",
    tag: "eazio-f10",
    name: __ez("تسجيل دخول"),
    type: "variant",
    priceA: 5,
    priceB: 15,
    labelA: __ez("بسيط"),
    labelB: __ez("احترافي"),
    time: __ez("2–4 أيام"),
    desc: __ez("شاشة تسجيل الدخول للسيرفر.")
  }, {
    id: "f11",
    tag: "eazio-f11",
    name: __ez("قائمة منيو"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("تصميم قائمة المنيو الرئيسية للسيرفر.")
  }, {
    id: "f12",
    tag: "eazio-f12",
    name: __ez("قائمة رقصات"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("تصميم قائمة اختيار الرقصات.")
  }, {
    id: "f13",
    tag: "eazio-f13",
    name: __ez("خريطة ميني ماب"),
    type: "fixed",
    price: 12,
    time: __ez("2–3 أيام"),
    desc: __ez("تصميم ميني ماب مخصص لخريطة سيرفرك.")
  }, {
    id: "f14",
    tag: "eazio-f14",
    name: __ez("إضافة منطقة للخريطة"),
    type: "fixed",
    price: 2,
    time: __ez("1 يوم"),
    desc: __ez("إضافة منطقة جديدة لخريطة السيرفر.")
  }],
  discord: [{
    id: "d01",
    tag: "eazio-d01",
    name: __ez("شعار رسم"),
    type: "variant",
    priceA: 30,
    priceB: 50,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("3–5 أيام"),
    desc: __ez("شعار مرسوم بأسلوب فني مميز لسيرفر الديسكورد.")
  }, {
    id: "d02",
    tag: "eazio-d02",
    name: __ez("شعار رمزي"),
    type: "variant",
    priceA: 20,
    priceB: 30,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("2–4 أيام"),
    desc: __ez("شعار رمزي بسيط وقابل للتذكر.")
  }, {
    id: "d03",
    tag: "eazio-d03",
    name: __ez("شعار أحرف"),
    type: "variant",
    priceA: 15,
    priceB: 25,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–3 أيام"),
    desc: __ez("شعار مبني على أحرف اسم السيرفر.")
  }, {
    id: "d04",
    tag: "eazio-d04",
    name: __ez("بانر ديسكورد"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("بانر ترويجي للسيرفر على الديسكورد.")
  }, {
    id: "d05",
    tag: "eazio-d05",
    name: __ez("بانر بروفايل"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("بانر البروفايل الشخصي.")
  }, {
    id: "d06",
    tag: "eazio-d06",
    name: __ez("خط فاصل"),
    type: "variant",
    priceA: 3,
    priceB: 5,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1 يوم"),
    desc: __ez("خط فاصل جمالي بين قنوات السيرفر.")
  }, {
    id: "d07",
    tag: "eazio-d07",
    name: __ez("خلفية PC"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("خلفية مخصصة للكمبيوتر بهوية السيرفر.")
  }, {
    id: "d08",
    tag: "eazio-d08",
    name: __ez("ترحيب ديسكورد"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("رسالة ترحيب بصرية للأعضاء الجدد.")
  }, {
    id: "d09",
    tag: "eazio-d09",
    name: __ez("صور بوتات"),
    type: "fixed",
    price: 2,
    time: __ez("1 يوم"),
    desc: __ez("تصميم صور وأيقونات البوتات.")
  }, {
    id: "d10",
    tag: "eazio-d10",
    name: __ez("صور قائمة قوانين"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("تصميم قائمة قوانين السيرفر.")
  }, {
    id: "d11",
    tag: "eazio-d11",
    name: __ez("صور تذكرة"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("تصميم بطاقة نظام التذاكر.")
  }, {
    id: "d12",
    tag: "eazio-d12",
    name: __ez("صور إنفو"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("تصميم صور المعلومات والإحصائيات.")
  }, {
    id: "d13",
    tag: "eazio-d13",
    name: __ez("بانر إنفايت"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("بانر دعوة السيرفر.")
  }],
  stream: [{
    id: "s01",
    tag: "eazio-s01",
    name: __ez("شعار رسم"),
    type: "variant",
    priceA: 30,
    priceB: 50,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("3–5 أيام"),
    desc: __ez("شعار مرسوم بأسلوب فني مميز لقناتك.")
  }, {
    id: "s02",
    tag: "eazio-s02",
    name: __ez("شعار رمزي"),
    type: "variant",
    priceA: 20,
    priceB: 30,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("2–4 أيام"),
    desc: __ez("شعار رمزي بسيط وقابل للتذكر.")
  }, {
    id: "s03",
    tag: "eazio-s03",
    name: __ez("شعار أحرف"),
    type: "variant",
    priceA: 15,
    priceB: 25,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–3 أيام"),
    desc: __ez("شعار مبني على أحرف اسم القناة.")
  }, {
    id: "s04",
    tag: "eazio-s04",
    name: __ez("سكرين Starting"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("شاشة بداية البث.")
  }, {
    id: "s05",
    tag: "eazio-s05",
    name: __ez("سكرين Ending"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("شاشة نهاية البث.")
  }, {
    id: "s06",
    tag: "eazio-s06",
    name: __ez("سكرين Right Back"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("شاشة الغياب المؤقت.")
  }, {
    id: "s07",
    tag: "eazio-s07",
    name: __ez("سكرين Just Chatting"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("شاشة الدردشة فقط.")
  }, {
    id: "s08",
    tag: "eazio-s08",
    name: __ez("سكرين Offline"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("شاشة وضع القناة offline.")
  }, {
    id: "s09",
    tag: "eazio-s09",
    name: __ez("إطار فيس كام"),
    type: "variant",
    priceA: 5,
    priceB: 10,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–2 يوم"),
    desc: __ez("إطار الكاميرا خلال البث.")
  }, {
    id: "s10",
    tag: "eazio-s10",
    name: __ez("انتقالية 2D"),
    type: "fixed",
    price: 5,
    time: __ez("1–2 يوم"),
    desc: __ez("انتقالية ثنائية الأبعاد بين مشاهد البث.")
  }, {
    id: "s11",
    tag: "eazio-s11",
    name: __ez("مستويات اشتراك"),
    type: "fixed",
    price: 10,
    time: __ez("2–3 أيام"),
    desc: __ez("تصميم أيقونات ومستويات الاشتراك.")
  }, {
    id: "s12",
    tag: "eazio-s12",
    name: __ez("بانل"),
    type: "fixed",
    price: 2,
    time: __ez("1 يوم"),
    desc: __ez("بانل معلومات القناة.")
  }, {
    id: "s13",
    tag: "eazio-s13",
    name: __ez("إنترو 3D"),
    type: "fixed",
    price: 20,
    time: __ez("3–5 أيام"),
    desc: __ez("مقدمة بث ثلاثية الأبعاد احترافية.")
  }],
  stores: [{
    id: "e01",
    tag: "eazio-e01",
    name: __ez("شعار رسم"),
    type: "variant",
    priceA: 30,
    priceB: 50,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("3–5 أيام"),
    desc: __ez("شعار مرسوم بأسلوب فني مميز لمتجرك.")
  }, {
    id: "e02",
    tag: "eazio-e02",
    name: __ez("شعار رمزي"),
    type: "variant",
    priceA: 20,
    priceB: 30,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("2–4 أيام"),
    desc: __ez("شعار رمزي بسيط وقابل للتذكر.")
  }, {
    id: "e03",
    tag: "eazio-e03",
    name: __ez("شعار أحرف"),
    type: "variant",
    priceA: 15,
    priceB: 25,
    labelA: __ez("ثابت"),
    labelB: __ez("متحرك"),
    time: __ez("1–3 أيام"),
    desc: __ez("شعار مبني على أحرف اسم المتجر.")
  }, {
    id: "e04",
    tag: "eazio-e04",
    name: __ez("بنر أساسي"),
    type: "fixed",
    price: 10,
    time: __ez("1–2 يوم"),
    desc: __ez("بنر رئيسي للصفحة الأمامية.")
  }, {
    id: "e05",
    tag: "eazio-e05",
    name: __ez("بنر قسم"),
    type: "fixed",
    price: 10,
    time: __ez("1–2 يوم"),
    desc: __ez("بنر مخصص لأقسام المتجر.")
  }, {
    id: "e06",
    tag: "eazio-e06",
    name: __ez("بنر منتج"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("بنر عرض للمنتج.")
  }, {
    id: "e07",
    tag: "eazio-e07",
    name: __ez("بنر طرق الدفع"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("بنر طرق الدفع المتاحة.")
  }, {
    id: "e08",
    tag: "eazio-e08",
    name: __ez("بنر دعم فني"),
    type: "fixed",
    price: 5,
    time: __ez("1 يوم"),
    desc: __ez("بنر قسم الدعم الفني.")
  }, {
    id: "e09",
    tag: "eazio-e09",
    name: __ez("فاصل متحرك"),
    type: "fixed",
    price: 10,
    time: __ez("2–3 أيام"),
    desc: __ez("فاصل متحرك جمالي بين أقسام المتجر.")
  }]
};
const PACKAGES = {
  fivem: [{
    id: "vp1",
    tag: "eazio-vp1",
    subtype: "VRP",
    name: "VRP 1",
    products: [__ez("شعار رسم ثابت"), __ez("بنر ديسكورد ثابت"), __ez("بانر FiveM ثابت"), __ez("بانر تحميل ثابت"), __ez("جوال ثابت"), __ez("قائمة رقصات ثابتة"), __ez("ترحيب ديسكورد"), __ez("خط فاصل ثابت"), __ez("5 إعلانات ثابتة")],
    price: 50,
    originalPrice: 78,
    badge: __ez("وفّر $28"),
    color: "#f97316",
    desc: __ez("باقة VRP شاملة بتصاميم ثابتة تغطي كل احتياجات سيرفر الرول بلاي.")
  }, {
    id: "vp2",
    tag: "eazio-vp2",
    subtype: "VRP",
    name: "VRP 2",
    products: [__ez("شعار رسم ثابت ومتحرك"), __ez("بنر ديسكورد متحرك"), __ez("بانر FiveM متحرك"), __ez("بانر تحميل متحرك"), __ez("جوال متحرك"), __ez("قائمة رقصات متحركة"), __ez("خط فاصل متحرك"), __ez("ترحيب ديسكورد"), __ez("7 إعلانات متحركة")],
    price: 100,
    originalPrice: 148,
    badge: __ez("وفّر $48"),
    color: "#f97316",
    desc: __ez("باقة VRP احترافية بتصاميم متحركة كاملة لسيرفر رول بلاي متميز.")
  }, {
    id: "vp3",
    tag: "eazio-vp3",
    subtype: "VRP",
    name: "VRP 3",
    products: [__ez("شعار رسم ثابت ومتحرك"), __ez("بنر ديسكورد متحرك"), __ez("بانر FiveM متحرك"), __ez("بانر تحميل متحرك"), __ez("جوال متحرك"), __ez("قائمة رقصات متحركة"), __ez("خط فاصل متحرك"), __ez("ترحيب ديسكورد"), __ez("10 إعلانات متحركة"), __ez("3 إنذارات متحركة"), __ez("لودينج سكرين بسيط")],
    price: 150,
    originalPrice: 228,
    badge: __ez("وفّر $78"),
    color: "#f97316",
    desc: __ez("الباقة الذهبية لـ VRP — كل شيء متحرك واحترافي بأعلى مستوى.")
  }, {
    id: "ex1",
    tag: "eazio-ex1",
    subtype: "ESX",
    name: "ESX 1",
    products: [__ez("شعار رمزي ثابت"), __ez("بنر ديسكورد ثابت"), __ez("بانر FiveM ثابت"), __ez("بانر تحميل ثابت"), __ez("ترحيب ديسكورد")],
    price: 35,
    originalPrice: 50,
    badge: __ez("وفّر $15"),
    color: "#f97316",
    desc: __ez("باقة ESX أساسية بتصاميم ثابتة وشعار رمزي مميز.")
  }, {
    id: "ex2",
    tag: "eazio-ex2",
    subtype: "ESX",
    name: "ESX 2",
    products: [__ez("شعار رمزي ثابت ومتحرك"), __ez("بنر ديسكورد متحرك"), __ez("بانر FiveM متحرك"), __ez("بانر تحميل متحرك"), __ez("ترحيب ديسكورد")],
    price: 55,
    originalPrice: 75,
    badge: __ez("وفّر $20"),
    color: "#f97316",
    desc: __ez("باقة ESX متقدمة بتصاميم متحركة وشعار رمزي بنسختيه.")
  }, {
    id: "fw1",
    tag: "eazio-fw1",
    subtype: "CFW",
    name: "CFW 1",
    products: [__ez("شعار أحرف ثابت"), __ez("بنر ديسكورد ثابت"), __ez("بانر FiveM ثابت"), __ez("بانر تحميل ثابت"), __ez("ترحيب ديسكورد")],
    price: 30,
    originalPrice: 43,
    badge: __ez("وفّر $13"),
    color: "#f97316",
    desc: __ez("باقة CFW أساسية بتصاميم ثابتة تعطي سيرفرك مظهرًا احترافيًا.")
  }, {
    id: "fw2",
    tag: "eazio-fw2",
    subtype: "CFW",
    name: "CFW 2",
    products: [__ez("شعار أحرف ثابت ومتحرك"), __ez("بنر ديسكورد متحرك"), __ez("بانر FiveM متحرك"), __ez("بانر تحميل متحرك"), __ez("ترحيب ديسكورد")],
    price: 50,
    originalPrice: 70,
    badge: __ez("وفّر $20"),
    color: "#f97316",
    desc: __ez("باقة CFW متقدمة بتصاميم متحركة تضفي حيوية على سيرفرك.")
  }],
  discord: [{
    id: "dc1",
    tag: "eazio-dc1",
    subtype: null,
    name: __ez("ديسكورد 1"),
    products: [__ez("شعار أحرف ثابت ومتحرك"), __ez("بنر ديسكورد ثابت ومتحرك"), __ez("صور بوتات"), __ez("خط فاصل ثابت ومتحرك"), __ez("بنر إنفايت"), __ez("ترحيب ديسكورد"), __ez("صورة قوانين")],
    price: 50,
    originalPrice: 73,
    badge: __ez("وفّر $23"),
    color: "#5865F2",
    desc: __ez("باقة ديسكورد أساسية تغطي الهوية البصرية الكاملة لسيرفرك.")
  }, {
    id: "dc2",
    tag: "eazio-dc2",
    subtype: null,
    name: __ez("ديسكورد 2"),
    products: [__ez("شعار أحرف ثابت ومتحرك"), __ez("بنر ديسكورد ثابت ومتحرك"), __ez("صور بوتات"), __ez("خط فاصل ثابت ومتحرك"), __ez("بنر إنفايت"), __ez("ترحيب ديسكورد"), __ez("صورة قوانين"), __ez("بنر بروفايل ثابت ومتحرك"), __ez("صورة تذكرة ثابتة"), __ez("خلفية دسكتوب ثابتة ومتحركة")],
    price: 75,
    originalPrice: 108,
    badge: __ez("وفّر $33"),
    color: "#5865F2",
    desc: __ez("باقة ديسكورد متوسطة بإضافة بروفايل وتذكرة وخلفية دسكتوب.")
  }, {
    id: "dc3",
    tag: "eazio-dc3",
    subtype: null,
    name: __ez("ديسكورد 3"),
    products: [__ez("شعار أحرف ثابت ومتحرك"), __ez("بنر ديسكورد ثابت ومتحرك"), __ez("صور بوتات"), __ez("3 خطوط فاصلة ثابتة ومتحركة"), __ez("بنر إنفايت"), __ez("ترحيب ديسكورد"), __ez("5 صور قوانين"), __ez("3 بنرات بروفايل"), __ez("صورة تذكرة ثابتة"), __ez("خلفية دسكتوب ثابتة ومتحركة")],
    price: 100,
    originalPrice: 148,
    badge: __ez("وفّر $48"),
    color: "#5865F2",
    desc: __ez("الباقة الذهبية للديسكورد — هوية بصرية متكاملة بأعلى مستوى.")
  }],
  stream: [{
    id: "st1",
    tag: "eazio-st1",
    subtype: __ez("كيك/تويتش"),
    name: __ez("ستريم 1"),
    products: [__ez("شعار أحرف ثابت ومتحرك"), __ez("بنر ثابت"), __ez("Starting متحركة"), __ez("Ending متحركة"), __ez("Right Back متحركة"), __ez("Offline ثابتة"), __ez("Just Chatting متحركة"), __ez("انتقالية متحركة"), __ez("إطار كام متحرك"), __ez("5 بانل 2D")],
    price: 80,
    originalPrice: 115,
    badge: __ez("وفّر $35"),
    color: "#9146ff",
    desc: __ez("باقة ستريم أساسية لمنصة كيك أو تويتش بكل الشاشات والمؤثرات.")
  }, {
    id: "st2",
    tag: "eazio-st2",
    subtype: __ez("كيك/تويتش"),
    name: __ez("ستريم 2"),
    products: [__ez("شعار رمزي ثابت ومتحرك"), __ez("إنترو 3D"), __ez("بنر ثابت"), __ez("Starting متحركة"), __ez("Ending متحركة"), __ez("Right Back متحركة"), __ez("Offline ثابتة"), __ez("Just Chatting متحركة"), __ez("انتقالية متحركة"), __ez("إطار كام متحرك"), __ez("5 بانل 3D"), __ez("مستويات اشتراك")],
    price: 100,
    originalPrice: 155,
    badge: __ez("وفّر $55"),
    color: "#9146ff",
    desc: __ez("باقة ستريم احترافية مع إنترو 3D وبانلات ثلاثية الأبعاد.")
  }, {
    id: "st3",
    tag: "eazio-st3",
    subtype: __ez("كيك/تويتش"),
    name: __ez("ستريم 3"),
    products: [__ez("شعار رسم ثابت ومتحرك"), __ez("إنترو 3D"), __ez("بنر ثابت"), __ez("Starting متحركة"), __ez("Ending متحركة"), __ez("Right Back متحركة"), __ez("Offline ثابتة"), __ez("Just Chatting متحركة"), __ez("إطار كام متحرك"), __ez("بانل 3D حسب الطلب"), __ez("مستويات اشتراك"), __ez("Alert فولو 2D")],
    price: 150,
    originalPrice: 220,
    badge: __ez("وفّر $70"),
    color: "#9146ff",
    desc: __ez("الباقة الذهبية للستريم — شعار رسم + 3D + ألرت فولو.")
  }, {
    id: "tt1",
    tag: "eazio-tt1",
    subtype: __ez("تيك توك"),
    name: __ez("تيك توك 1"),
    products: [__ez("شعار أحرف/رمزي ثابت ومتحرك"), __ez("Starting متحركة"), __ez("Ending متحركة"), __ez("Right Back متحركة"), __ez("Just Chatting متحركة"), __ez("انتقالية متحركة"), __ez("بانل رابط الدعم في البايو"), __ez("Alert آخر داعم"), __ez("Alert أكبر داعم")],
    price: 80,
    originalPrice: 115,
    badge: __ez("وفّر $35"),
    color: "#ff0050",
    desc: __ez("باقة تيك توك أساسية مع شاشات كاملة وألرتات الدعم.")
  }, {
    id: "tt2",
    tag: "eazio-tt2",
    subtype: __ez("تيك توك"),
    name: __ez("تيك توك 2"),
    products: [__ez("شعار رسم ثابت ومتحرك"), __ez("Starting متحركة"), __ez("Ending متحركة"), __ez("Right Back متحركة"), __ez("Just Chatting متحركة"), __ez("انتقالية متحركة"), __ez("بانل رابط الدعم في البايو"), __ez("Alert آخر داعم"), __ez("Alert أكبر داعم")],
    price: 100,
    originalPrice: 140,
    badge: __ez("وفّر $40"),
    color: "#ff0050",
    desc: __ez("باقة تيك توك متقدمة مع شعار رسم احترافي.")
  }, {
    id: "yt1",
    tag: "eazio-yt1",
    subtype: __ez("يوتيوب"),
    name: __ez("يوتيوب 1"),
    products: [__ez("شعار أحرف/رمزي ثابت ومتحرك"), __ez("بانر ثابت"), __ez("إنترو"), "End Screen", __ez("5 ثيمنيل")],
    price: 80,
    originalPrice: 110,
    badge: __ez("وفّر $30"),
    color: "#ff0000",
    desc: __ez("باقة يوتيوب أساسية بشعار وبانر وإنترو وثيمنيلات.")
  }, {
    id: "yt2",
    tag: "eazio-yt2",
    subtype: __ez("يوتيوب"),
    name: __ez("يوتيوب 2"),
    products: [__ez("شعار رسم ثابت ومتحرك"), __ez("بانر ثابت"), __ez("إنترو"), "End Screen", __ez("5 ثيمنيل")],
    price: 100,
    originalPrice: 140,
    badge: __ez("وفّر $40"),
    color: "#ff0000",
    desc: __ez("باقة يوتيوب احترافية مع شعار رسم مميز.")
  }],
  stores: [{
    id: "sh1",
    tag: "eazio-sh1",
    subtype: null,
    name: __ez("سلة 1"),
    products: [__ez("شعار أحرف/رمزي ثابت"), __ez("بنر أساسي للموقع"), __ez("5 بنرات أقسام"), __ez("10 تصاميم منتجات"), __ez("بنر دعم فني"), __ez("بنر طرق دفع")],
    price: 100,
    originalPrice: 145,
    badge: __ez("وفّر $45"),
    color: "#10b981",
    desc: __ez("باقة متجر أساسية تغطي الهوية البصرية الكاملة لمتجرك الإلكتروني.")
  }, {
    id: "sh2",
    tag: "eazio-sh2",
    subtype: null,
    name: __ez("سلة 2"),
    products: [__ez("شعار أحرف/رمزي ثابت"), __ez("4 بنرات أساسية للموقع"), __ez("10 بنرات أقسام"), __ez("30 تصاميم منتجات"), __ez("بنر دعم فني"), __ez("بنر طرق دفع"), __ez("فاصل متحرك")],
    price: 170,
    originalPrice: 245,
    badge: __ez("وفّر $75"),
    color: "#10b981",
    desc: __ez("باقة متجر متوسطة مع محتوى أكثر وفاصل متحرك.")
  }, {
    id: "sh3",
    tag: "eazio-sh3",
    subtype: null,
    name: __ez("سلة 3"),
    products: [__ez("شعار أحرف/رمزي ثابت"), __ez("7 بنرات أساسية للموقع"), __ez("20 بنرات أقسام"), __ez("50 تصاميم منتجات"), __ez("بنر دعم فني"), __ez("بنر طرق دفع"), __ez("5 فواصل متحركة")],
    price: 300,
    originalPrice: 430,
    badge: __ez("وفّر $130"),
    color: "#10b981",
    desc: __ez("الباقة الذهبية للمتاجر الكبيرة — تغطية شاملة لكل محتوى المتجر.")
  }]
};

// خريطة المنتجات المعلّمة بـ eazio من Shopify — مفتاحها التاج الخاص (eazio-vp1 مثلاً)
const shopifyByTag = {};
(window.EAZIO_DATA || []).forEach(cat => {
  (cat.products || []).forEach(p => {
    (p.tags || []).forEach(t => {
      shopifyByTag[t] = p;
    });
  });
});
const hasShopifyData = Object.keys(shopifyByTag).length > 0;
const STATIC_CATS = CATEGORIES.map(cat => ({
  ...cat,
  products: [
  // المنتجات الفردية
  ...(INDIVIDUAL_PRODUCTS[cat.id] || []).filter(p => !hasShopifyData || shopifyByTag[p.tag]).map(p => {
    const sp = shopifyByTag[p.tag];
    if (!sp) return p;
    return {
      ...p,
      price: Math.round(sp.price / 100),
      priceA: Math.round((sp.price1 || sp.price) / 100),
      priceB: sp.price2 ? Math.round(sp.price2 / 100) : Math.round((sp.price1 || sp.price) / 100),
      variantId: vid(sp.vid1),
      variantIdA: vid(sp.vid1),
      variantIdB: vid(sp.vid2) || null
    };
  }),
  // الباقات
  ...(PACKAGES[cat.id] || []).filter(pkg => !hasShopifyData || shopifyByTag[pkg.tag]).map(pkg => {
    const sp = shopifyByTag[pkg.tag];
    const spItems = sp ? parsePackageItems(sp.descHtml || sp.desc || "") : [];
    const price = sp ? Math.round(sp.price / 100) : pkg.price;
    const orig = sp && sp.comparePrice > sp.price ? Math.round(sp.comparePrice / 100) : pkg.originalPrice;
    const saved = Math.max(0, orig - price);
    return {
      ...pkg,
      isPackage: true,
      products: spItems.length > 0 ? spItems : pkg.products,
      price,
      originalPrice: orig,
      badge: saved > 0 ? `${__ez("وفّر")} $${saved}` : pkg.badge,
      variantId: sp ? vid(sp.vid1) : null,
      handle: sp ? sp.handle : pkg.handle
    };
  })]
}));

// ─── Dynamic Shopify data ────────────────────────────────────────────────────
const colorToGlow = hex => {
  try {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16),
      g = parseInt(h.slice(2, 4), 16),
      b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.35)`;
  } catch (e) {
    return "rgba(107,94,245,0.35)";
  }
};
const parsePackageItems = html => {
  if (!html) return [];
  const liMatches = [...html.matchAll(/<li[^>]*>(.*?)<\/li>/gis)];
  if (liMatches.length) return liMatches.map(m => m[1].replace(/<[^>]+>/g, "").trim()).filter(Boolean);
  const plain = html.replace(/<[^>]+>/g, "").trim();
  const byPlus = plain.split(/\s*\+\s*/).filter(Boolean);
  if (byPlus.length > 1) return byPlus;
  return plain.split(/[\n\r]+/).map(s => s.trim()).filter(Boolean);
};
const vid = s => s && s !== "0" && String(s).length > 1 ? String(s) : null;

// جيب variant ID من Shopify AJAX لو مش موجود في البيانات
const fetchFirstVariantId = async handle => {
  try {
    const res = await fetch(`/products/${handle}.js`);
    const data = await res.json();
    return data.variants?.[0]?.id ? String(data.variants[0].id) : null;
  } catch (e) {
    return null;
  }
};
const buildProduct = (p, catColor) => {
  const p1 = Math.round(p.price / 100);
  const vid1 = vid(p.vid1);
  const vid2 = vid(p.vid2);
  const isVariant = !!vid2; // لو في vid2 يبقى عنده نسختين

  if (p.isPackage) {
    const orig = p.comparePrice > p.price ? Math.round(p.comparePrice / 100) : p1;
    const saved = Math.max(0, orig - p1);
    return {
      id: p.id,
      handle: p.handle,
      name: p.name,
      desc: p.desc,
      products: parsePackageItems(p.descHtml || p.desc),
      price: p1,
      originalPrice: orig,
      badge: saved > 0 ? `${__ez("وفّر")} $${saved}` : "",
      color: catColor,
      variantId: vid1,
      subtype: p.subtype || "",
      isPackage: true
    };
  }
  if (isVariant) return {
    id: p.id,
    handle: p.handle,
    name: p.name,
    desc: p.desc,
    type: "variant",
    priceA: Math.round((p.price1 || p.price) / 100),
    priceB: Math.round((p.price2 || p.price) / 100),
    labelA: p.title1 || __ez("ثابت"),
    labelB: p.title2 || __ez("متحرك"),
    variantIdA: vid1,
    variantIdB: vid2,
    time: __ez(p.time || ""),
    isPackage: false
  };
  return {
    id: p.id,
    handle: p.handle,
    name: p.name,
    desc: p.desc,
    type: "fixed",
    price: p1,
    variantId: vid1,
    time: __ez(p.time || ""),
    isPackage: false
  };
};
const DYNAMIC_CATS = (window.EAZIO_DATA || []).map(cat => ({
  id: cat.id,
  label: __ez(cat.label),
  color: cat.color,
  glow: colorToGlow(cat.color),
  products: (cat.products || []).map(p => buildProduct(p, cat.color))
}));

// ─── Category Illustration ────────────────────────────────────────────────────
const CategoryIllustration = ({
  catId,
  accent
}) => {
  const ills = {
    fivem: /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 120 80",
      fill: "none",
      style: {
        width: "100%",
        height: "100%"
      }
    }, /*#__PURE__*/React.createElement("rect", {
      x: "10",
      y: "20",
      width: "100",
      height: "45",
      rx: "6",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.3",
      fill: accent,
      fillOpacity: "0.05"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "10",
      y: "20",
      width: "100",
      height: "10",
      rx: "6",
      fill: accent,
      fillOpacity: "0.15"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "18",
      cy: "25",
      r: "2.5",
      fill: accent,
      opacity: "0.5"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "26",
      cy: "25",
      r: "2.5",
      fill: accent,
      opacity: "0.5"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "34",
      cy: "25",
      r: "2.5",
      fill: accent,
      opacity: "0.5"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "20",
      y: "38",
      width: "35",
      height: "4",
      rx: "2",
      fill: accent,
      opacity: "0.4"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "20",
      y: "46",
      width: "50",
      height: "3",
      rx: "1.5",
      fill: accent,
      opacity: "0.2"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "75",
      y: "36",
      width: "26",
      height: "20",
      rx: "4",
      fill: accent,
      opacity: "0.12",
      stroke: accent,
      strokeWidth: "0.6",
      strokeOpacity: "0.35"
    }), /*#__PURE__*/React.createElement("polygon", {
      points: "88,42 93,49 83,49",
      fill: accent,
      opacity: "0.5"
    })),
    discord: /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 120 80",
      fill: "none",
      style: {
        width: "100%",
        height: "100%"
      }
    }, /*#__PURE__*/React.createElement("path", {
      d: "M48 24c-6.6 0-12 5.4-12 12v4l-8 8v-8c0-11 9-20 20-20h12c11 0 20 9 20 20v12c0 6.6-5.4 12-12 12H48l-8 8V60",
      stroke: accent,
      strokeWidth: "1.2",
      strokeOpacity: "0.5",
      fill: "none",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "52",
      cy: "38",
      r: "3",
      fill: accent,
      opacity: "0.6"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "62",
      cy: "38",
      r: "3",
      fill: accent,
      opacity: "0.6"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "72",
      cy: "38",
      r: "3",
      fill: accent,
      opacity: "0.6"
    })),
    stream: /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 120 80",
      fill: "none",
      style: {
        width: "100%",
        height: "100%"
      }
    }, /*#__PURE__*/React.createElement("rect", {
      x: "15",
      y: "18",
      width: "90",
      height: "50",
      rx: "6",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.3",
      fill: accent,
      fillOpacity: "0.05"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "60",
      cy: "43",
      r: "14",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.35"
    }), /*#__PURE__*/React.createElement("polygon", {
      points: "55,37 55,49 67,43",
      fill: accent,
      opacity: "0.55"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "15",
      y: "60",
      width: "90",
      height: "8",
      rx: "0 0 6 6",
      fill: accent,
      fillOpacity: "0.1"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "20",
      y: "63",
      width: "30",
      height: "2",
      rx: "1",
      fill: accent,
      opacity: "0.35"
    })),
    store: /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 120 80",
      fill: "none",
      style: {
        width: "100%",
        height: "100%"
      }
    }, /*#__PURE__*/React.createElement("rect", {
      x: "25",
      y: "35",
      width: "70",
      height: "30",
      rx: "4",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.35",
      fill: accent,
      fillOpacity: "0.06"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M25 39l10-15h50l10 15",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.4",
      fill: accent,
      fillOpacity: "0.08"
    }), /*#__PURE__*/React.createElement("line", {
      x1: "60",
      y1: "35",
      x2: "60",
      y2: "65",
      stroke: accent,
      strokeWidth: "0.8",
      strokeOpacity: "0.2"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "45",
      cy: "58",
      r: "4",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.4"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: "75",
      cy: "58",
      r: "4",
      stroke: accent,
      strokeWidth: "1",
      strokeOpacity: "0.4"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "50",
      y: "42",
      width: "20",
      height: "12",
      rx: "2",
      fill: accent,
      opacity: "0.15"
    }))
  };
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: "100%",
      height: 64,
      position: "relative",
      overflow: "hidden",
      background: `linear-gradient(135deg, ${accent}20 0%, ${accent}06 100%)`,
      borderRadius: "10px 10px 0 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px 14px",
      boxSizing: "border-box"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: "100%",
      height: "100%"
    }
  }, ills[catId] || ills.stores), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: 0,
      background: `radial-gradient(ellipse 70% 60% at 50% 50%, ${accent}14 0%, transparent 70%)`
    }
  }));
};

// ─── Variant Toggle ───────────────────────────────────────────────────────────
const VariantToggle = ({
  selected,
  onChange,
  labelA,
  labelB,
  priceA,
  priceB,
  accent
}) => /*#__PURE__*/React.createElement("div", {
  style: {
    display: "flex",
    gap: 0,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: 7,
    overflow: "hidden",
    marginBottom: 8
  }
}, [{
  key: "a",
  label: labelA,
  price: priceA
}, {
  key: "b",
  label: labelB,
  price: priceB
}].map(v => /*#__PURE__*/React.createElement("button", {
  key: v.key,
  onClick: () => onChange(v.key),
  style: {
    flex: 1,
    border: "none",
    cursor: "pointer",
    background: selected === v.key ? accent : "transparent",
    color: selected === v.key ? "#fff" : "rgba(250,250,250,0.45)",
    padding: "5px 4px 4px",
    fontFamily: "'Tajawal',sans-serif",
    transition: "background 0.18s ease, color 0.18s ease",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1
  }
}, /*#__PURE__*/React.createElement("span", {
  style: {
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1
  }
}, v.label), /*#__PURE__*/React.createElement("span", {
  style: {
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1,
    opacity: selected === v.key ? 1 : 0.6
  }
}, "$", v.price))));

// ─── Individual Card ──────────────────────────────────────────────────────────
const IndividualCard = ({
  product,
  catId,
  accent,
  onOpen
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [variant, setVariant] = React.useState("a");
  const [localLoading, setLocalLoading] = React.useState(false);
  const ref = React.useRef(null);
  const visible = useScrollReveal(ref, {
    margin: "-40px 0px"
  });
  const {
    addToCart,
    loading
  } = useCart();
  const displayPrice = product.type === "variant" ? variant === "a" ? product.priceA : product.priceB : product.price;
  const currentVariantId = product.type === "variant" ? variant === "a" ? product.variantIdA : product.variantIdB : product.variantId;
  const handleAddToCart = async () => {
    let vid = currentVariantId;
    if (!vid) {
      setLocalLoading(true);
      vid = await fetchFirstVariantId(product.handle);
      setLocalLoading(false);
    }
    if (vid) addToCart(vid);
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    style: {
      background: "#0d0b1a",
      borderRadius: 10,
      border: `1px solid ${hovered ? accent + "55" : "rgba(255,255,255,0.07)"}`,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      transition: `transform 0.22s ${ease}, box-shadow 0.22s ease, border-color 0.22s ease, opacity 0.45s ${ease}`,
      transform: hovered ? "translateY(-3px)" : "none",
      boxShadow: hovered ? `0 10px 28px rgba(0,0,0,0.4), 0 0 16px ${accent}20` : "0 1px 8px rgba(0,0,0,0.3)",
      opacity: visible ? 1 : 0
    }
  }, /*#__PURE__*/React.createElement(CategoryIllustration, {
    catId: catId,
    accent: accent
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "10px 12px 12px",
      flex: 1,
      display: "flex",
      flexDirection: "column"
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 13,
      fontWeight: 700,
      color: "#FAFAFA",
      margin: "0 0 4px",
      lineHeight: 1.3
    }
  }, product.name), product.type === "variant" ? /*#__PURE__*/React.createElement(VariantToggle, {
    selected: variant,
    onChange: setVariant,
    labelA: product.labelA,
    labelB: product.labelB,
    priceA: product.priceA,
    priceB: product.priceB,
    accent: accent
  }) : /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 8,
      display: "flex",
      alignItems: "baseline",
      gap: 4
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 18,
      fontWeight: 800,
      letterSpacing: "-0.03em",
      color: "#FAFAFA"
    }
  }, "$", product.price)), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "10",
    height: "10",
    viewBox: "0 0 11 11",
    fill: "none"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "5.5",
    cy: "5.5",
    r: "4.5",
    stroke: "rgba(255,255,255,0.2)",
    strokeWidth: "0.8"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.5 3.5v2.5l1.5 1",
    stroke: "rgba(255,255,255,0.2)",
    strokeWidth: "0.8",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 10,
      color: "rgba(250,250,250,0.28)"
    }
  }, product.time)), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: handleAddToCart,
    disabled: loading || localLoading,
    style: {
      flex: 1,
      background: hovered ? accent : `${accent}22`,
      color: hovered ? "#fff" : accent,
      border: `1px solid ${accent}55`,
      borderRadius: 6,
      padding: "6px 0",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
      opacity: loading || localLoading ? 0.6 : 1,
      transition: "background 0.18s, color 0.18s"
    }
  }, localLoading ? "..." : __ez("أضف إلى السلة")), /*#__PURE__*/React.createElement("button", {
    onClick: () => onOpen(product),
    style: {
      width: 28,
      background: "transparent",
      color: "rgba(250,250,250,0.3)",
      border: "1px solid rgba(255,255,255,0.09)",
      borderRadius: 6,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "border-color 0.15s, color 0.15s"
    },
    onMouseEnter: e => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)";
      e.currentTarget.style.color = "#FAFAFA";
    },
    onMouseLeave: e => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.09)";
      e.currentTarget.style.color = "rgba(250,250,250,0.3)";
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "11",
    height: "11",
    viewBox: "0 0 13 13",
    fill: "none"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "6.5",
    cy: "6.5",
    r: "5.5",
    stroke: "currentColor",
    strokeWidth: "1.1"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.5 5.5v4M6.5 4v.5",
    stroke: "currentColor",
    strokeWidth: "1.1",
    strokeLinecap: "round"
  }))))));
};

// ─── Package Card ─────────────────────────────────────────────────────────────
const PackageCard = ({
  pkg
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [localLoading, setLocalLoading] = React.useState(false);
  const ref = React.useRef(null);
  const visible = useScrollReveal(ref, {
    margin: "-40px 0px"
  });
  const {
    addToCart,
    loading
  } = useCart();
  const discount = Math.round((1 - pkg.price / pkg.originalPrice) * 100);
  const handleAddToCart = async () => {
    let vid = pkg.variantId;
    if (!vid) {
      setLocalLoading(true);
      vid = await fetchFirstVariantId(pkg.handle);
      setLocalLoading(false);
    }
    if (vid) addToCart(vid);
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    style: {
      background: hovered ? `linear-gradient(135deg, ${pkg.color}18 0%, #0d0b1a 60%)` : "#0d0b1a",
      borderRadius: 14,
      border: `1px solid ${hovered ? pkg.color + "55" : pkg.color + "28"}`,
      padding: "22px 22px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
      position: "relative",
      overflow: "hidden",
      transition: `transform 0.22s ${ease}, box-shadow 0.22s ease, background 0.22s ease, border-color 0.22s ease, opacity 0.45s ${ease}`,
      transform: hovered ? "translateY(-4px)" : "none",
      boxShadow: hovered ? `0 12px 40px rgba(0,0,0,0.5), 0 0 28px ${pkg.color}22` : "0 2px 12px rgba(0,0,0,0.3)",
      opacity: visible ? 1 : 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: -20,
      insetInlineEnd: -20,
      width: 72,
      height: 72,
      borderRadius: "50%",
      background: pkg.color,
      filter: "blur(28px)",
      opacity: hovered ? 0.22 : 0.1,
      transition: "opacity 0.3s",
      pointerEvents: "none"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 10
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      background: `${pkg.color}22`,
      border: `1px solid ${pkg.color}44`,
      borderRadius: 100,
      padding: "3px 10px",
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "9",
    height: "9",
    viewBox: "0 0 10 10",
    fill: pkg.color
  }, /*#__PURE__*/React.createElement("polygon", {
    points: "5,0.5 6.2,3.8 9.8,3.8 7,6 8,9.5 5,7.5 2,9.5 3,6 0.2,3.8 3.8,3.8"
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 11,
      fontWeight: 600,
      color: pkg.color
    }
  }, pkg.badge)), /*#__PURE__*/React.createElement("h3", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 800,
      color: "#FAFAFA",
      margin: 0,
      lineHeight: 1.3
    }
  }, pkg.name)), /*#__PURE__*/React.createElement("div", {
    style: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      flexShrink: 0,
      background: `${pkg.color}20`,
      border: `2px solid ${pkg.color}50`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 13,
      fontWeight: 800,
      color: pkg.color,
      lineHeight: 1
    }
  }, discount, "%"), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 9,
      color: `${pkg.color}99`,
      lineHeight: 1
    }
  }, __ez("خصم")))), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      lineHeight: 1.7,
      color: "rgba(250,250,250,0.42)",
      margin: 0
    }
  }, pkg.desc), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      gap: 5
    }
  }, pkg.products.map((p, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      alignItems: "center",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "12",
    height: "12",
    viewBox: "0 0 12 12",
    fill: "none",
    style: {
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "6",
    cy: "6",
    r: "5.5",
    fill: `${pkg.color}20`,
    stroke: `${pkg.color}50`,
    strokeWidth: "0.8"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.5 6l1.8 1.8L8.5 4",
    stroke: pkg.color,
    strokeWidth: "1.1",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12.5,
      color: "rgba(250,250,250,0.62)",
      lineHeight: 1.5
    }
  }, p)))), /*#__PURE__*/React.createElement("div", {
    style: {
      height: 1,
      background: "rgba(255,255,255,0.06)"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "baseline",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 24,
      fontWeight: 800,
      letterSpacing: "-0.04em",
      color: "#FAFAFA"
    }
  }, "$", pkg.price), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 500,
      color: "rgba(250,250,250,0.25)",
      textDecoration: "line-through"
    }
  }, "$", pkg.originalPrice)), /*#__PURE__*/React.createElement("button", {
    onClick: handleAddToCart,
    disabled: loading || localLoading,
    style: {
      background: pkg.color,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "9px 18px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      opacity: loading || localLoading ? 0.6 : 1,
      boxShadow: `0 4px 14px ${pkg.color}44`,
      transition: `transform 0.18s ${ease}, box-shadow 0.18s ease, opacity 0.18s`
    },
    onMouseEnter: e => {
      if (!loading && !localLoading) {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 6px 18px ${pkg.color}66`;
      }
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = `0 4px 14px ${pkg.color}44`;
    }
  }, localLoading ? "..." : __ez("أضف إلى السلة"))));
};

// ─── Product Detail Modal ─────────────────────────────────────────────────────
const ProductModal = ({
  product,
  accent,
  onClose
}) => {
  const {
    addToCart,
    loading
  } = useCart();
  const [modalVariant, setModalVariant] = React.useState("a");
  const [localLoading, setLocalLoading] = React.useState(false);
  React.useEffect(() => {
    const fn = e => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);
  const modalVariantId = product.type === "variant" ? modalVariant === "a" ? product.variantIdA : product.variantIdB : product.variantId;
  const handleAddToCart = async () => {
    let vid = modalVariantId;
    if (!vid) {
      setLocalLoading(true);
      vid = await fetchFirstVariantId(product.handle);
      setLocalLoading(false);
    }
    if (vid) {
      addToCart(vid);
      onClose();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    onClick: onClose,
    style: {
      position: "fixed",
      inset: 0,
      zIndex: 500,
      background: "rgba(2,1,5,0.78)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      animation: "fadeIn 0.2s ease"
    }
  }, /*#__PURE__*/React.createElement("div", {
    onClick: e => e.stopPropagation(),
    style: {
      background: "#0d0b1a",
      border: `1px solid ${accent}44`,
      borderRadius: 16,
      padding: "28px 28px 24px",
      maxWidth: 460,
      width: "100%",
      boxShadow: `0 24px 80px rgba(0,0,0,0.7), 0 0 40px ${accent}20`,
      animation: "slideUp 0.25s ease",
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onClose,
    style: {
      position: "absolute",
      top: 14,
      insetInlineStart: 14,
      width: 28,
      height: 28,
      borderRadius: 7,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.1)",
      color: "rgba(250,250,250,0.45)",
      cursor: "pointer",
      fontSize: 15,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "×"), /*#__PURE__*/React.createElement("div", {
    style: {
      height: 3,
      background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
      borderRadius: 2,
      marginBottom: 20
    }
  }), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 20,
      fontWeight: 800,
      color: "#FAFAFA",
      margin: "0 0 8px"
    }
  }, product.name), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      lineHeight: 1.75,
      color: "rgba(250,250,250,0.45)",
      margin: "0 0 18px"
    }
  }, product.desc), product.type === "variant" ? /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginBottom: 18
    }
  }, [{
    key: "a",
    label: product.labelA,
    price: product.priceA
  }, {
    key: "b",
    label: product.labelB,
    price: product.priceB
  }].map(v => /*#__PURE__*/React.createElement("button", {
    key: v.key,
    onClick: () => setModalVariant(v.key),
    style: {
      background: modalVariant === v.key ? `${accent}22` : `${accent}09`,
      border: `1px solid ${modalVariant === v.key ? accent : accent + "30"}`,
      borderRadius: 10,
      padding: "12px 14px",
      textAlign: "center",
      cursor: "pointer",
      transition: "background 0.18s, border-color 0.18s"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 12,
      color: modalVariant === v.key ? accent : `${accent}99`,
      marginBottom: 4
    }
  }, v.label), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 24,
      fontWeight: 800,
      color: "#FAFAFA"
    }
  }, "$", v.price)))) : /*#__PURE__*/React.createElement("div", {
    style: {
      background: `${accent}10`,
      border: `1px solid ${accent}30`,
      borderRadius: 10,
      padding: "12px 14px",
      marginBottom: 18,
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 11,
      color: `${accent}99`,
      marginBottom: 2
    }
  }, __ez("السعر")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 28,
      fontWeight: 800,
      color: "#FAFAFA"
    }
  }, "$", product.price)), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 7,
      marginBottom: 20,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 8,
      padding: "9px 12px"
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "13",
    height: "13",
    viewBox: "0 0 13 13",
    fill: "none"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "6.5",
    cy: "6.5",
    r: "5.5",
    stroke: "rgba(255,255,255,0.3)",
    strokeWidth: "0.9"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.5 4v3l1.8 1.8",
    stroke: "rgba(255,255,255,0.3)",
    strokeWidth: "0.9",
    strokeLinecap: "round"
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "rgba(250,250,250,0.38)"
    }
  }, __ez("وقت التسليم: "), /*#__PURE__*/React.createElement("strong", {
    style: {
      color: "rgba(250,250,250,0.68)"
    }
  }, product.time))), /*#__PURE__*/React.createElement("button", {
    onClick: handleAddToCart,
    disabled: loading || localLoading,
    style: {
      width: "100%",
      background: accent,
      color: "#fff",
      border: "none",
      borderRadius: 9,
      padding: "13px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 700,
      cursor: "pointer",
      opacity: loading || localLoading ? 0.6 : 1,
      boxShadow: `0 4px 18px ${accent}50`,
      transition: `transform 0.18s ${ease}, box-shadow 0.18s ease, opacity 0.18s`
    },
    onMouseEnter: e => {
      if (!loading && !localLoading) {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 6px 22px ${accent}70`;
      }
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = `0 4px 18px ${accent}50`;
    }
  }, localLoading ? __ez("جاري التحميل...") : __ez("أضف إلى السلة"))));
};

// ─── Main Products Section ────────────────────────────────────────────────────
const ProductsAR = () => {
  const {
    isMobile,
    isTablet
  } = useBreakpoint();
  const cats = DYNAMIC_CATS.length > 0 ? DYNAMIC_CATS : STATIC_CATS;
  const [activeCategory, setActiveCategory] = React.useState(cats[0]?.id || "");
  const [activeTab, setActiveTab] = React.useState("packages");
  const [activeSubtype, setActiveSubtype] = React.useState(null);
  const [modalProduct, setModalProduct] = React.useState(null);
  const [catIndicator, setCatIndicator] = React.useState({
    left: 4,
    width: 80
  });
  const catBtnRefs = React.useRef([]);
  const headerRef = React.useRef(null);
  const headerVisible = useScrollReveal(headerRef, {
    margin: "-60px 0px"
  });
  const cat = cats.find(c => c.id === activeCategory) || cats[0];
  const allProducts = cat?.products || [];
  const individuals = allProducts.filter(p => !p.isPackage);
  const allPackages = allProducts.filter(p => p.isPackage);
  const subtypes = [...new Set(allPackages.map(p => p.subtype).filter(Boolean))];
  const hasSubtypes = subtypes.length > 0;

  // عند تغيير الفئة، اختر VRP أولاً في fivem — غير كده أول subtype
  React.useEffect(() => {
    const pkgs = (cats.find(c => c.id === activeCategory)?.products || []).filter(p => p.isPackage);
    const subs = [...new Set(pkgs.map(p => p.subtype).filter(Boolean))];
    const preferred = activeCategory === "fivem" && subs.includes("VRP") ? "VRP" : subs[0] || null;
    setActiveSubtype(preferred);
  }, [activeCategory]);
  const filteredPackages = hasSubtypes && activeSubtype ? allPackages.filter(p => p.subtype === activeSubtype) : allPackages;
  React.useEffect(() => {
    const idx = cats.findIndex(c => c.id === activeCategory);
    const el = catBtnRefs.current[idx];
    if (el) setCatIndicator({
      left: el.offsetLeft,
      width: el.offsetWidth
    });
  }, [activeCategory, cats.length]);
  return /*#__PURE__*/React.createElement("section", {
    id: "services",
    style: {
      padding: `96px ${isMobile ? "16px" : "40px"}`,
      maxWidth: 1280,
      margin: "0 auto"
    },
    "data-pricing": "true"
  }, modalProduct && /*#__PURE__*/React.createElement(ProductModal, {
    product: modalProduct,
    accent: cat?.color || "#6b5ef5",
    onClose: () => setModalProduct(null)
  }), cats.length === 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      padding: "80px 40px",
      color: "rgba(250,250,250,0.3)",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15
    }
  }, __ez("أضف فئات من إعدادات السكشن في الشوبيفاي لعرض المنتجات")), /*#__PURE__*/React.createElement("div", {
    ref: headerRef,
    style: {
      marginBottom: 44,
      opacity: headerVisible ? 1 : 0,
      transform: `translateY(${headerVisible ? 0 : 28}px)`,
      transition: prefersReducedMotion ? "none" : `opacity 0.7s ${ease}, transform 0.7s ${ease}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "rgba(59,45,188,0.1)",
      border: "1px solid rgba(59,45,188,0.25)",
      borderRadius: 100,
      padding: "5px 12px",
      marginBottom: 18
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      fontWeight: 500,
      color: "#7b6ef0"
    }
  }, __ez("الخدمات"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: 24,
      flexWrap: "wrap"
    }
  }, /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(28px,4vw,48px)",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      color: "#FAFAFA",
      margin: 0,
      lineHeight: 1.2
    }
  }, __ez("منتجات فردية وباقات مخصصة")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 15,
      lineHeight: 1.8,
      color: "rgba(250,250,250,0.38)",
      maxWidth: 340,
      margin: 0
    }
  }, __ez("اختر قسمك، تصفّح الباقات أو اختر منتجًا فرديًا.")))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 32
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      position: "relative",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 11,
      padding: 4,
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      maxWidth: "100%",
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: 4,
      bottom: 4,
      left: catIndicator.left,
      width: catIndicator.width,
      background: cat?.color,
      borderRadius: 8,
      boxShadow: `0 2px 12px ${cat?.color}55`,
      transition: prefersReducedMotion ? "none" : `left 0.28s ${easeIO}, width 0.28s ${easeIO}, background 0.3s ease`,
      pointerEvents: "none",
      zIndex: 0
    }
  }), cats.map((c, i) => /*#__PURE__*/React.createElement("button", {
    key: c.id,
    ref: el => catBtnRefs.current[i] = el,
    onClick: () => setActiveCategory(c.id),
    style: {
      position: "relative",
      zIndex: 1,
      background: "transparent",
      border: "none",
      borderRadius: 8,
      padding: "9px 20px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 700,
      color: activeCategory === c.id ? "#FAFAFA" : "rgba(250,250,250,0.38)",
      cursor: "pointer",
      transition: "color 0.18s ease",
      whiteSpace: "nowrap"
    }
  }, c.label))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8
    }
  }, [{
    id: "packages",
    label: __ez("باقات")
  }, {
    id: "individual",
    label: __ez("منتجات فردية")
  }].map(t => /*#__PURE__*/React.createElement("button", {
    key: t.id,
    onClick: () => setActiveTab(t.id),
    style: {
      background: activeTab === t.id ? cat?.color : "transparent",
      color: activeTab === t.id ? "#FAFAFA" : "rgba(250,250,250,0.4)",
      border: `1px solid ${activeTab === t.id ? cat?.color : "rgba(255,255,255,0.1)"}`,
      borderRadius: 8,
      padding: "8px 20px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
      transition: "background 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s",
      boxShadow: activeTab === t.id ? `0 2px 10px ${cat?.color}44` : "none"
    }
  }, t.label)))), activeTab === "packages" && hasSubtypes && /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8,
      marginBottom: 28
    }
  }, subtypes.map(sub => {
    const sc = cat?.color || "#6b5ef5";
    const active = activeSubtype === sub;
    return /*#__PURE__*/React.createElement("button", {
      key: sub,
      onClick: () => setActiveSubtype(sub),
      style: {
        background: active ? sc : "transparent",
        color: active ? "#FAFAFA" : "rgba(250,250,250,0.45)",
        border: `1px solid ${active ? sc : "rgba(255,255,255,0.1)"}`,
        borderRadius: 8,
        padding: "7px 20px",
        fontFamily: "'Tajawal',sans-serif",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s",
        boxShadow: active ? `0 2px 10px ${sc}44` : "none"
      }
    }, sub);
  })), activeTab === "individual" ? /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: isMobile ? "repeat(2,1fr)" : isTablet ? "repeat(3,1fr)" : "repeat(auto-fill, minmax(200px, 1fr))",
      gap: 12
    }
  }, individuals.length === 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      gridColumn: "1/-1",
      textAlign: "center",
      padding: "64px 40px",
      border: `1px dashed ${cat?.color}33`,
      borderRadius: 14,
      background: `${cat?.color}08`
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 16,
      fontWeight: 600,
      color: "rgba(250,250,250,0.35)",
      margin: 0
    }
  }, __ez("لا توجد منتجات فردية بعد"))), individuals.map(p => /*#__PURE__*/React.createElement(IndividualCard, {
    key: p.id,
    product: p,
    catId: activeCategory,
    accent: cat?.color || "#6b5ef5",
    onOpen: setModalProduct
  }))) : /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
      gap: 16
    }
  }, filteredPackages.length > 0 ? filteredPackages.map(pkg => /*#__PURE__*/React.createElement(PackageCard, {
    key: pkg.id,
    pkg: pkg
  })) : /*#__PURE__*/React.createElement("div", {
    style: {
      gridColumn: "1/-1",
      textAlign: "center",
      padding: "64px 40px",
      border: `1px dashed ${cat?.color}33`,
      borderRadius: 14,
      background: `${cat?.color}08`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 32,
      marginBottom: 12,
      opacity: 0.5
    }
  }, "📦"), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 16,
      fontWeight: 600,
      color: "rgba(250,250,250,0.35)",
      margin: 0
    }
  }, __ez("الباقات قادمة قريبًا")))));
};

// ─── Process Section ──────────────────────────────────────────────────────────
const ProcessAR = () => {
  const {
    isMobile,
    isTablet
  } = useBreakpoint();
  const ref = React.useRef(null);
  const inView = useScrollReveal(ref, {
    margin: "-60px 0px"
  });
  const steps = [{
    n: "01",
    title: __ez("اختر"),
    desc: __ez("تصفّح قائمة الخدمات. لكل خدمة نطاق محدد وسعر واضح — اختر ما يناسب احتياجك بالضبط."),
    icon: /*#__PURE__*/React.createElement("svg", {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "3",
      y: "3",
      width: "8",
      height: "8",
      rx: "2",
      stroke: "#7b6ef0",
      strokeWidth: "1.5"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "13",
      y: "3",
      width: "8",
      height: "8",
      rx: "2",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      opacity: "0.5"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "3",
      y: "13",
      width: "8",
      height: "8",
      rx: "2",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      opacity: "0.5"
    }), /*#__PURE__*/React.createElement("rect", {
      x: "13",
      y: "13",
      width: "8",
      height: "8",
      rx: "2",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      opacity: "0.3"
    }))
  }, {
    n: "02",
    title: __ez("أرسل المتطلبات"),
    desc: __ez("أكمل استمارة المتطلبات المُصممة بعناية. نسألك الأسئلة الصحيحة حتى لا تضطر لكتابة مواصفات من الصفر."),
    icon: /*#__PURE__*/React.createElement("svg", {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "4",
      y: "2",
      width: "16",
      height: "20",
      rx: "3",
      stroke: "#7b6ef0",
      strokeWidth: "1.5"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M8 8h8M8 12h8M8 16h5",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      opacity: "0.6"
    }))
  }, {
    n: "03",
    title: __ez("التصميم"),
    desc: __ez("نبدأ العمل فورًا. جولة تعديل واحدة مُدرجة في السعر، ومعظم المشاريع تنتهي من المرة الأولى."),
    icon: /*#__PURE__*/React.createElement("svg", {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M12 20h9",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/React.createElement("path", {
      d: "M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))
  }, {
    n: "04",
    title: __ez("التسليم"),
    desc: __ez("تستلم جميع ملفات المصدر وصيغ التصدير والتوثيقات في الموعد المحدد. بلا تأخير ولا مبررات."),
    icon: /*#__PURE__*/React.createElement("svg", {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/React.createElement("polyline", {
      points: "7,10 12,15 17,10",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/React.createElement("line", {
      x1: "12",
      y1: "15",
      x2: "12",
      y2: "3",
      stroke: "#7b6ef0",
      strokeWidth: "1.5",
      strokeLinecap: "round"
    }))
  }];
  return /*#__PURE__*/React.createElement("section", {
    style: {
      padding: `96px ${isMobile ? "16px" : "40px"}`,
      borderTop: "1px solid rgba(255,255,255,0.05)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 1200,
      margin: "0 auto"
    }
  }, /*#__PURE__*/React.createElement(RevealSection, {
    style: {
      textAlign: "center",
      marginBottom: 64
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "rgba(59,45,188,0.1)",
      border: "1px solid rgba(59,45,188,0.25)",
      borderRadius: 100,
      padding: "5px 12px",
      marginBottom: 20
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      fontWeight: 500,
      color: "#7b6ef0"
    }
  }, __ez("آلية العمل"))), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(28px,3.5vw,44px)",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      color: "#FAFAFA",
      margin: "0 auto 16px",
      maxWidth: 480,
      lineHeight: 1.25
    }
  }, __ez("من المتطلبات إلى التسليم في أيام")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 16,
      color: "rgba(250,250,250,0.4)",
      margin: 0,
      lineHeight: 1.7
    }
  }, __ez("بدون اجتماعات تحضيرية ولا مدير مشروع وسيط."))), /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: {
      display: "grid",
      gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
      gap: isMobile ? 32 : 2,
      position: "relative"
    }
  }, !isMobile && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: 44,
      insetInlineStart: "12.5%",
      insetInlineEnd: "12.5%",
      height: 1,
      background: "linear-gradient(90deg, transparent, rgba(59,45,188,0.4) 20%, rgba(59,45,188,0.4) 80%, transparent)"
    }
  }), steps.map((step, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "0 20px",
      position: "relative",
      zIndex: 1,
      opacity: inView ? 1 : 0,
      transform: `translateY(${inView ? 0 : 24}px)`,
      transition: prefersReducedMotion ? "none" : `opacity 0.65s ${ease} ${i * 0.1}s, transform 0.65s ${ease} ${i * 0.1}s`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 80,
      height: 80,
      borderRadius: "50%",
      background: "#0d0b1a",
      border: "1px solid rgba(111,105,169,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 24,
      position: "relative",
      boxShadow: "0 4px 24px rgba(0,0,0,0.4), 0 0 0 4px rgba(59,45,188,0.08)"
    }
  }, step.icon, /*#__PURE__*/React.createElement("span", {
    style: {
      position: "absolute",
      top: -4,
      insetInlineEnd: -4,
      width: 22,
      height: 22,
      borderRadius: "50%",
      background: "#3b2dbc",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 10,
      fontWeight: 700,
      color: "white"
    }
  }, step.n)), /*#__PURE__*/React.createElement("h3", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 18,
      fontWeight: 700,
      color: "#FAFAFA",
      margin: "0 0 10px"
    }
  }, step.title), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      lineHeight: 1.8,
      color: "rgba(250,250,250,0.4)",
      margin: 0
    }
  }, step.desc))))));
};

// ─── Marquee ──────────────────────────────────────────────────────────────────
const MarqueeStrip = ({
  items
}) => {
  const trackRef = React.useRef(null);
  const posRef = React.useRef(0);
  const rafRef = React.useRef(null);
  const SPEED = 0.6;
  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || prefersReducedMotion) return;
    const spans = track.querySelectorAll("span");
    const halfCount = items.length;
    let halfWidth = 0;
    const getHalfWidth = () => {
      let w = 0;
      for (let i = 0; i < halfCount; i++) w += spans[i].getBoundingClientRect().width;
      w += 64 * (halfCount - 1);
      return w;
    };
    const animate = () => {
      if (!halfWidth) halfWidth = getHalfWidth();
      posRef.current += SPEED;
      if (posRef.current >= halfWidth + 64) posRef.current -= halfWidth + 64;
      track.style.transform = `translateX(${posRef.current}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [items]);
  const all = [...items, ...items, ...items, ...items];
  return /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: "hidden",
      width: "100%",
      maskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
      WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: trackRef,
    style: {
      display: "flex",
      alignItems: "center",
      gap: 64,
      width: "max-content",
      willChange: "transform"
    }
  }, all.map((item, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 600,
      color: "rgba(250,250,250,0.28)",
      whiteSpace: "nowrap",
      flexShrink: 0
    }
  }, item))));
};

// ─── Testimonial Card ─────────────────────────────────────────────────────────
const TestimonialCard = ({
  testimonial: t
}) => {
  const [hovered, setHovered] = React.useState(false);
  return /*#__PURE__*/React.createElement("div", {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    style: {
      background: "#0d0b1a",
      border: `1px solid ${hovered ? "rgba(111,105,169,0.3)" : "rgba(255,255,255,0.07)"}`,
      borderRadius: 14,
      padding: "28px 28px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 20,
      height: "100%",
      transform: hovered ? "translateY(-3px)" : "none",
      transition: `transform 0.25s ${ease}, border-color 0.25s ease`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 3
    }
  }, [...Array(5)].map((_, j) => /*#__PURE__*/React.createElement("svg", {
    key: j,
    width: "14",
    height: "14",
    viewBox: "0 0 14 14",
    fill: t.accent
  }, /*#__PURE__*/React.createElement("polygon", {
    points: "7,1 8.8,5.5 13.5,5.5 9.8,8.5 11.2,13 7,10.2 2.8,13 4.2,8.5 0.5,5.5 5.2,5.5"
  })))), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 15,
      lineHeight: 1.85,
      color: "rgba(250,250,250,0.65)",
      margin: 0,
      flex: 1
    }
  }, "\"", t.quote, "\""), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 38,
      height: 38,
      borderRadius: "50%",
      flexShrink: 0,
      background: `linear-gradient(135deg, ${t.accent}66, ${t.accent}22)`,
      border: `1px solid ${t.accent}44`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 13,
      fontWeight: 700,
      color: "#FAFAFA"
    }
  }, t.initials), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 14,
      fontWeight: 600,
      color: "#FAFAFA"
    }
  }, t.name), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      color: "rgba(250,250,250,0.35)",
      marginTop: 2
    }
  }, t.role))));
};

// ─── Testimonials Section ─────────────────────────────────────────────────────
const TESTIMONIALS = [{
  quote: __ez("أرسلنا متطلبات الهوية البصرية يوم الاثنين، ووصلت الملفات الكاملة يوم الجمعة. الجودة كانت على مستوى كبريات شركات التصميم، لكن بجزء بسيط من التكلفة."),
  name: __ez("سارة أوكونكوو"),
  role: __ez("شريكة مؤسِّسة، Luma Health"),
  initials: __ez("سأ"),
  accent: "#6b5ef5"
}, {
  quote: __ez("استمارة المتطلبات كانت دقيقة ومدروسة. حين وصل المفهوم الأول شعرت أنهم قرأوا أفكارنا. جولة تعديل واحدة وانتهى الأمر بنتيجة مثالية."),
  name: __ez("مارك تيبو"),
  role: __ez("مدير المنتج، Motif Studio"),
  initials: __ez("مت"),
  accent: "#5344a4"
}, {
  quote: __ez("جربت منصات توظيف المصممين من قبل. eazio فئة مختلفة تمامًا — نطاق محدد، تسعير صريح، ومواعيد تسليم حقيقية. أنجزت معهم ثلاثة مشاريع حتى الآن."),
  name: __ez("جيمس يو"),
  role: __ez("مؤسس، Orbit SaaS"),
  initials: __ez("جي"),
  accent: "#7b6ef0"
}];
const TestimonialsAR = () => {
  const {
    isMobile,
    isTablet
  } = useBreakpoint();
  return /*#__PURE__*/React.createElement("section", {
    style: {
      padding: `96px ${isMobile ? "16px" : "40px"}`,
      borderTop: "1px solid rgba(255,255,255,0.05)",
      position: "relative",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: 0,
      left: "50%",
      transform: "translateX(-50%)",
      width: 600,
      height: 400,
      pointerEvents: "none",
      background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(59,45,188,0.15) 0%, transparent 70%)"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 1200,
      margin: "0 auto"
    }
  }, /*#__PURE__*/React.createElement(RevealSection, {
    style: {
      textAlign: "center",
      marginBottom: 56
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      background: "rgba(59,45,188,0.1)",
      border: "1px solid rgba(59,45,188,0.25)",
      borderRadius: 100,
      padding: "5px 12px",
      marginBottom: 20
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      fontWeight: 500,
      color: "#7b6ef0"
    }
  }, __ez("آراء العملاء"))), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(28px,3.5vw,44px)",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      color: "#FAFAFA",
      margin: 0,
      lineHeight: 1.25
    }
  }, __ez("عملاء أنجزوا مشاريعهم أسرع"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)",
      gap: 16,
      marginBottom: 56
    }
  }, TESTIMONIALS.map((t, i) => /*#__PURE__*/React.createElement(RevealSection, {
    key: i,
    delay: i * 0.1,
    style: {
      height: "100%"
    }
  }, /*#__PURE__*/React.createElement(TestimonialCard, {
    testimonial: t
  })))), /*#__PURE__*/React.createElement("div", {
    style: {
      paddingTop: 48,
      borderTop: "1px solid rgba(255,255,255,0.05)"
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      color: "rgba(250,250,250,0.25)",
      letterSpacing: "0.06em",
      margin: "0 0 20px",
      textAlign: "center"
    }
  }, __ez("تثق بنا فرق من شركات")), /*#__PURE__*/React.createElement(MarqueeStrip, {
    items: ["Luma Health", "Motif Studio", "Orbit SaaS", "Vela Labs", "Carto Systems", "Nomi", "Arcline", "Forma", "Beacon", "Solus"]
  }))));
};

// ─── Final CTA ────────────────────────────────────────────────────────────────
const FinalCTAAR = () => {
  const {
    isMobile
  } = useBreakpoint();
  const discordUrl = window.EAZIO_CONFIG?.discordUrl || "#";
  const whatsappUrl = window.EAZIO_CONFIG?.whatsappUrl || "#";
  return /*#__PURE__*/React.createElement("section", {
    style: {
      padding: `${isMobile ? "60px 16px" : "80px 40px 96px"}`,
      borderTop: "1px solid rgba(255,255,255,0.05)"
    }
  }, /*#__PURE__*/React.createElement(RevealSection, null, /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 800,
      margin: "0 auto",
      background: "linear-gradient(135deg, rgba(59,45,188,0.22) 0%, rgba(83,68,164,0.12) 60%, transparent 100%)",
      border: "1px solid rgba(59,45,188,0.3)",
      borderRadius: 20,
      padding: isMobile ? "40px 24px" : "72px 64px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: "-30%",
      left: "50%",
      transform: "translateX(-50%)",
      width: 400,
      height: 300,
      pointerEvents: "none",
      background: "radial-gradient(ellipse, rgba(59,45,188,0.35) 0%, transparent 70%)"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 24,
      display: "flex",
      justifyContent: "center"
    }
  }, /*#__PURE__*/React.createElement(EazioMark, {
    size: 80
  })), /*#__PURE__*/React.createElement("h2", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: "clamp(28px,4vw,50px)",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
      color: "#FAFAFA",
      margin: "0 0 20px"
    }
  }, __ez("جاهز لبدء مشروعك القادم؟")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 17,
      lineHeight: 1.8,
      color: "rgba(250,250,250,0.45)",
      margin: "0 auto 40px",
      maxWidth: 440
    }
  }, __ez("اختر الخدمة المناسبة وأرسل متطلباتك. معظم المشاريع تُسلَّم في غضون أسبوع.")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 12,
      justifyContent: "center",
      flexWrap: "wrap"
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: discordUrl,
    target: "_blank",
    rel: "noopener",
    style: {
      background: "#3b2dbc",
      color: "#FAFAFA",
      border: "none",
      borderRadius: 10,
      padding: "15px 36px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 700,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      boxShadow: "0 4px 20px rgba(59,45,188,0.45)",
      transition: `transform 0.2s ${ease}, box-shadow 0.2s ease`
    },
    onMouseEnter: e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 10px 36px rgba(59,45,188,0.65)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(59,45,188,0.45)";
    }
  }, __ez("سيرفر الديسكورد")), /*#__PURE__*/React.createElement("a", {
    href: whatsappUrl,
    target: "_blank",
    rel: "noopener",
    style: {
      background: "transparent",
      color: "rgba(250,250,250,0.55)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 10,
      padding: "15px 28px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 15,
      fontWeight: 500,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-block",
      transition: "border-color 0.18s ease, background 0.18s ease, color 0.18s ease"
    },
    onMouseEnter: e => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
      e.currentTarget.style.color = "#FAFAFA";
    },
    onMouseLeave: e => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
      e.currentTarget.style.background = "transparent";
      e.currentTarget.style.color = "rgba(250,250,250,0.55)";
    }
  }, __ez("تواصل معنا")))))));
};

// ─── Footer ───────────────────────────────────────────────────────────────────
const FooterAR = () => {
  const {
    isMobile,
    isTablet
  } = useBreakpoint();
  const cols = [{
    heading: __ez("الخدمات"),
    links: [__ez("تصميم شعار"), __ez("هوية بصرية"), __ez("تصميم مواقع"), __ez("تصميم واجهات"), __ez("تصميم تطبيقات"), __ez("تصميم تغليف"), __ez("تصميم مطبوعات"), __ez("رسوم توضيحية")]
  }, {
    heading: __ez("الشركة"),
    links: [__ez("عنّا"), __ez("الأعمال"), __ez("آلية العمل"), __ez("وظائف"), __ez("للصحافة")]
  }, {
    heading: __ez("موارد"),
    links: [__ez("مدونة التصميم"), __ez("دراسات الحالة"), __ez("أدلة الأسلوب"), __ez("قوالب Figma"), __ez("الأسئلة الشائعة")]
  }, {
    heading: __ez("قانوني"),
    links: [__ez("سياسة الخصوصية"), __ez("شروط الاستخدام"), __ez("سياسة ملفات الارتباط"), __ez("سياسة الاسترداد")]
  }];
  return /*#__PURE__*/React.createElement(RevealSection, null, /*#__PURE__*/React.createElement("footer", {
    style: {
      borderTop: "1px solid rgba(255,255,255,0.06)",
      padding: `64px ${isMobile ? "16px" : "40px"} 40px`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      maxWidth: 1200,
      margin: "0 auto"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "1fr 1fr 1fr" : "2fr 1fr 1fr 1fr 1fr",
      gap: isMobile ? 24 : 40,
      marginBottom: 56
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      gridColumn: isMobile ? "1 / -1" : undefined
    }
  }, /*#__PURE__*/React.createElement(EazioLogo, {
    size: 26
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      lineHeight: 1.8,
      color: "rgba(250,250,250,0.35)",
      marginTop: 16,
      maxWidth: 240
    }
  }, __ez("خدمات تصميم كمنتج. نطاق واضح، سعر ثابت، ومواعيد حقيقية. مدعوم من فليكس.")), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 24
    }
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 12,
      fontWeight: 500,
      color: "rgba(250,250,250,0.4)",
      marginBottom: 10
    }
  }, __ez("ابقَ على اطلاع")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "email",
    placeholder: __ez("بريدك الإلكتروني"),
    style: {
      flex: 1,
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 8,
      padding: "9px 14px",
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "#FAFAFA",
      outline: "none",
      textAlign: EZ_RTL ? "right" : "left",
      transition: "border-color 0.2s"
    },
    onFocus: e => e.target.style.borderColor = "rgba(59,45,188,0.5)",
    onBlur: e => e.target.style.borderColor = "rgba(255,255,255,0.1)"
  }), /*#__PURE__*/React.createElement("button", {
    style: {
      background: "#3b2dbc",
      color: "#FAFAFA",
      border: "none",
      borderRadius: 8,
      padding: "9px 16px",
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer"
    }
  }, __ez("اشترك")))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 10,
      marginTop: 20
    }
  }, ["tw", "li", "dr", "ig"].map(s => /*#__PURE__*/React.createElement("button", {
    key: s,
    style: {
      width: 34,
      height: 34,
      borderRadius: 8,
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "rgba(250,250,250,0.35)",
      fontFamily: "monospace",
      fontSize: 11,
      fontWeight: 700,
      transition: "background 0.15s, border-color 0.15s"
    },
    onMouseEnter: e => {
      e.currentTarget.style.background = "rgba(255,255,255,0.09)";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
    },
    onMouseLeave: e => {
      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    }
  }, s)))), cols.map(col => /*#__PURE__*/React.createElement("div", {
    key: col.heading
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      fontFamily: "'Tajawal',sans-serif",
      fontSize: 12,
      fontWeight: 600,
      color: "rgba(250,250,250,0.5)",
      margin: "0 0 16px"
    }
  }, col.heading), /*#__PURE__*/React.createElement("ul", {
    style: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 10
    }
  }, col.links.map(l => /*#__PURE__*/React.createElement("li", {
    key: l
  }, /*#__PURE__*/React.createElement("a", {
    href: "#",
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 14,
      color: "rgba(250,250,250,0.35)",
      textDecoration: "none",
      transition: "color 0.15s"
    },
    onMouseEnter: e => e.target.style.color = "#FAFAFA",
    onMouseLeave: e => e.target.style.color = "rgba(250,250,250,0.35)"
  }, l))))))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      flexDirection: isMobile ? "column-reverse" : "row",
      justifyContent: "space-between",
      gap: 12,
      paddingTop: 24,
      borderTop: "1px solid rgba(255,255,255,0.06)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 24
    }
  }, [__ez("الخصوصية"), __ez("الشروط"), __ez("ملفات الارتباط")].map(l => /*#__PURE__*/React.createElement("a", {
    key: l,
    href: "#",
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "rgba(250,250,250,0.25)",
      textDecoration: "none"
    }
  }, l))), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: "'IBM Plex Sans Arabic',sans-serif",
      fontSize: 13,
      color: "rgba(250,250,250,0.2)"
    }
  }, "Eazio — Powered By Flix.")))));
};

// ─── App Root ─────────────────────────────────────────────────────────────────
const AppAR = () => /*#__PURE__*/React.createElement(CartProvider, null, /*#__PURE__*/React.createElement("div", {
  style: {
    background: "#020105",
    minHeight: "100vh",
    color: "#FAFAFA",
    direction: EZ_RTL ? "rtl" : "ltr"
  }
}, /*#__PURE__*/React.createElement(AnnouncementBar, null), /*#__PURE__*/React.createElement(Header, null), /*#__PURE__*/React.createElement(Hero, null), /*#__PURE__*/React.createElement("div", {
  style: {
    height: 1,
    margin: "0 40px",
    background: "linear-gradient(90deg, transparent, rgba(59,45,188,0.35) 30%, rgba(59,45,188,0.35) 70%, transparent)"
  }
}), /*#__PURE__*/React.createElement(ProductsAR, null), /*#__PURE__*/React.createElement(ProcessAR, null), /*#__PURE__*/React.createElement(TestimonialsAR, null), /*#__PURE__*/React.createElement(FinalCTAAR, null), /*#__PURE__*/React.createElement(FooterAR, null), /*#__PURE__*/React.createElement(CartDrawer, null), /*#__PURE__*/React.createElement(CartFloatBtn, null)));
const root = ReactDOM.createRoot(document.getElementById("eazio-root"));
root.render(/*#__PURE__*/React.createElement(AppAR, null));